import { useEffect, useMemo, useState, useRef } from 'react';
import { api, prepareImageUpload } from '../api.js';
import {
  effectiveParams,
  effectiveParam,
  paramStatusFor,
  paramDeltaLabel,
  hannaPhosphorusPpbToPo4Ppm,
  alkPhotoReadToDkh,
  DOSABLE_TANK_PARAMS,
} from '../params.js';
import { getDisplayUnits, formatTemp, fToC } from '../units.js';
import { formatCardValue } from '../param-history.js';
import { envelopeBreached, envelopeNote } from '../param-envelope.js';
import { pillRangeTitle, quickAddUnitLabel } from '../shown-units.js';
import { dropKitHint } from '../drop-kit-hint.js';
import {
  groupReadings,
  daysFromNow,
  supplyForecast,
  equipmentSupplyItems,
  equipmentPartItems,
  equipmentServiceItems,
  relativeTimeShort,
  isReadingStale,
  assessFreshnessClient,
  currentReadingsClient,
  outrankedTridentClient,
  daysSinceReading,
  isAutomatedMethodClient,
} from '../utils.js';
import { Sparkline } from './Sparkline.jsx';
import { CoralMark } from './CoralMark.jsx';
import { SetPasswordBanner } from './SetPasswordBanner.jsx';
import { EmailChangeNote } from './EmailChangeNote.jsx';
import { NotificationNudgeBanner } from './NotificationNudgeBanner.jsx';
import { SenseiScore } from './SenseiScore.jsx';
import { PhotoTimeline } from './PhotoTimeline.jsx';
import { JournalSection } from './JournalSection.jsx';
import { LivestockResidents } from './LivestockResidents.jsx';
import { DoseCalculator } from './DoseCalculator.jsx';
import { HydrosLiveTile } from './HydrosLiveTile.jsx';
import { WaterChangeSheet } from './WaterChangeSheet.jsx';
import { WhatsNewCard } from './WhatsNewCard.jsx';
import { AskSheet, FollowUpSheet, openFollowUps, followUpPlanIdByTask } from './CareView.jsx';
import { TipCard } from './TipCard.jsx';
import { isTaskLapsed } from '../../../shared/task-schedule.js';
import { COURSE_CHECK_TITLE } from '../../../shared/task-explainers.js';
import {
  SALINITY_UNITS,
  readSalinityUnit,
  writeSalinityUnit,
  salinityToPpt,
  isPlausibleSalinity,
  formatSalinity,
  salinityUnitLabel,
  formatSalinityRange,
  sgDecimals,
  endSentence,
} from '../salinity.js';
import { actionableUpdateFor, staleConcernText } from '../../../shared/sensei-action.js';
import { outlierMessage } from '../outlier.js';
import { courseProgress } from '../../../shared/treatment.js';
import { localDateIso } from '../local-date.js';
import { computeTrend } from '../../../shared/trend.js';
import { startReorderDrag, reorderPreview } from '../drag-reorder.js';
import { resolveReorderUrl } from '../reorder.js';
import { useConfig } from '../config.js';
import { track } from '../events.js';
import { ParamExplainerSheet } from './ParamExplainerSheet.jsx';
import { explainParam } from '../../../shared/param-explainers.js';
import { tankSubtype } from '../../../shared/tank-subtype.js';
import { photoMethodReads, photoReadable } from '../photo-params.js';
import { isCycleQuiet, isGradedParam, isQuarantineTank } from '../../../shared/tank-purpose.js';
import { CycleCardsNote } from './CycleCardsNote.jsx';
import { implausibleReading } from '../../../shared/reading-plausibility.js';
import { readingsAgree } from '../../../shared/instrument-margin.js';
import { isOwnParam } from '../../../shared/own-param.js';
import { OPEN_SETTINGS_EVENT, OPEN_ACCOUNT_EVENT, HYDROS_CHANGED_EVENT } from '../app-open.js';
import { unlinkedControllerRow } from '../hydros-inputs.js';
import { quietDeviceRow } from '../devices.js';
import { localDateString } from '../fill-date.js';
import { testTimeUnknown } from '../../../shared/trident-trust.js';
import { confirmPromptFor } from '../reading-confirm.js';
import { firstRunHint, isFreshwaterTank } from '../first-run.js';
import { showsReefFact } from '../saltwater-tools.js';
import {
  DEFAULT_LAYOUT,
  decodeLayout,
  encodeLayout,
  layoutKey,
  pickInitialLayout,
} from '../home-layout.js';

// Tank param keys (alk/calcium/magnesium), not the dose API's alk/ca/mg
// — the param cards iterate effectiveParams, which yields tank keys.
// Using the dose keys meant the DOSE chip never appeared on the Calcium
// or Magnesium cards.
const DOSABLE_PARAMS = new Set(['alk', 'calcium', 'magnesium']);

// Compact source labels for the param-card meta line + the history-
// sheet badges. The reading row carries a `method` set at save time
// (apex / hydros / manual / hanna / strip / refract / icp / computed);
// these strings drive the small chip the user sees so a glance at the
// card answers "is this number from my Apex probe or did I type it
// in?" — same anti-ambiguity move as the freshness stamp.
const SOURCE_LABEL = {
  apex: 'APEX',
  hydros: 'HYDROS',
  manual: 'MANUAL',
  hanna: 'HANNA',
  strip: 'STRIP',
  refract: 'REFRACT',
  icp: 'ICP',
  import: 'IMPORTED',
  computed: 'COMPUTED',
};
const PRO_MODE_KEY = 'aqs.proMode';
const LAYOUT_KEY = 'aqs.homeLayout';
const PARAM_COLS_KEY = 'aqs.paramCols';
const SECTION_LABELS = {
  score: 'Sensei Score',
  controller: 'HYDROS controller',
  photos: 'Photo timeline',
  today: 'Today',
  welcome: 'Welcome card',
  params: 'Parameters',
  journal: 'Journal',
  residents: 'In this tank',
  whatsnew: "What's new",
  tip: 'Fact of the day',
};

const shortDate = (value) =>
  new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

// This tab's own layout saves. HomeView unmounts whenever a param's
// history opens or the tab changes, and the user object it remounts with
// can predate the last save, so the last save is kept here (per account)
// and wins until the server copy moves on. pickInitialLayout has the rule.
const layoutSync = { session: null, inFlight: 0 };

function readCachedLayout() {
  try {
    const parsed = JSON.parse(localStorage.getItem(LAYOUT_KEY) || 'null');
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

// The quick add's "Are you sure?" opens under the kit field, below the fold
// on a phone with the keyboard up. A keeper who saw nothing happen taps Save
// again, which counts as yes: 84 dKH typed for 8.4 would be saved.
function revealPrompt(el) {
  el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
}

// A reading the server flagged as unusual this week, as Today and the card
// both read it. retestOnly: say "worth a retest" and nothing else, either
// because the reading is in range or because it is alk / Ca / Mg not yet
// confirmed by a second off-band test on the same side (acting on a misread
// there means dosing on a wrong number). The card offered DOSE beside a
// flagged 0.84 dKH while Today, above it, held it at a retest.
function outlierHold(tank, p, series) {
  const latest = series[0];
  if (
    !latest?.outlier_reason ||
    testTimeUnknown(latest, p.key) ||
    !isGradedParam(tank, p.key) ||
    (latest.ts ? new Date(latest.ts).getTime() : 0) < Date.now() - 7 * 86400000
  ) {
    return null;
  }
  // The server's reason is a clause that already ends "Worth a retest."
  const reason = String(outlierMessage(latest, tank)).trim();
  const sentence = `${reason.charAt(0).toUpperCase()}${reason.slice(1)}${/[.!?]$/.test(reason) ? '' : '.'}`;
  const offBand = paramStatusFor(tank, p.key, latest.value) === 'off';
  const [blo, bhi] = p.acceptable || [];
  const prev = series[1];
  const confirmedBySecond =
    offBand &&
    prev &&
    paramStatusFor(tank, p.key, prev.value) === 'off' &&
    prev.value < blo === latest.value < blo &&
    prev.value > bhi === latest.value > bhi &&
    readingsAgree(p.key, prev.value, latest.value);
  return {
    sentence,
    retestOnly: !offBand || (DOSABLE_TANK_PARAMS.includes(p.key) && !confirmedBySecond),
  };
}

// Edit-mode grip — a small unlabelled handle in the section's top-right
// while the user is rearranging Home. Grab it with mouse or finger and
// drag the section where you want it; arrow keys (when focused) move
// one step for keyboard users. The section name lives in the tooltip +
// aria-label rather than on screen — a labelled pill on every section
// read as bulky chrome.
function LayoutChrome({ k, onDragStart, move, onHide }) {
  return (
    <span className="hv-section-chrome">
      {onHide && (
        <button
          type="button"
          className="hv-section-hide font-heading"
          onClick={() => onHide(k)}
          aria-label={`Hide ${SECTION_LABELS[k]} from Home`}
        >
          Hide
        </button>
      )}
      <span
        role="button"
        tabIndex={0}
        className="hv-drag-handle compact"
        title={`Drag to move ${SECTION_LABELS[k]}`}
        onPointerDown={(e) => onDragStart(e, k)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            move(k, -1);
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            move(k, 1);
          }
        }}
        aria-label={`Drag to move ${SECTION_LABELS[k]}; arrow keys also work`}
      >
        <span className="hv-drag-grip" aria-hidden="true">
          ⠿
        </span>
      </span>
    </span>
  );
}

// A finger, not a mouse (audit 2026-10-01): the card's + was a 22 px
// target beside the 24 px "?", so a wet thumb aiming for one opened the
// other. The hit area is 44 px; the negative margin keeps the visible dot
// where it was in the card layout.
const CARD_ICON_HIT = {
  width: 44,
  height: 44,
  margin: -10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  padding: 0,
  flexShrink: 0,
};
const CARD_ICON_DOT = {
  width: 24,
  height: 24,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 16,
  lineHeight: 1,
};

const HEADER_CHIP_STYLE = {
  fontSize: 12,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  padding: '5px 12px',
  minHeight: 44,
  borderRadius: 999,
  border: '1px solid var(--surface-border)',
  background: 'transparent',
  color: 'var(--cyan)',
  cursor: 'pointer',
};

export function HomeView({
  tank,
  onTab,
  onReload,
  onOpenHistory,
  user,
  onRefreshUser,
  onOpenTankSettings,
  onOpenLivestock,
  onOpenLivestockHistory,
  onOpenIcp,
  onOpenAllHistory,
  onOpenImport,
  onOpenTreatments,
  onOpenAccount,
}) {
  const [doseParam, setDoseParam] = useState(null);
  const [careSheet, setCareSheet] = useState(null);
  const [explainKey, setExplainKey] = useState(null);
  const config = useConfig();
  const methodsForCfg = config?.capture?.methods_for_param || {};
  const METHOD_LABELS = {
    manual: 'Manual',
    hanna: 'Hanna',
    strip: 'Strip',
    refract: 'Refract',
    hydros: 'HYDROS',
  };
  const byParam = useMemo(() => groupReadings(tank.readings), [tank.readings]);
  // Effective params come from the tank itself (admin template ⊕ subtype
  // overrides ⊕ user's per-tank customisations). Hidden params are
  // filtered out for Home; the settings sheet can show + unhide them.
  const visibleParams = useMemo(() => effectiveParams(tank), [tank]);
  // Salinity display preference (ppt | PSU | S.G.). Cheap localStorage
  // read per render; settings changes land after a sheet close +
  // reload, which re-renders Home anyway. Storage stays ppt — this
  // converts on the way OUT only.
  const salinityUnit = readSalinityUnit();
  const displayUnits = getDisplayUnits(tank);

  // Whether any shown parameter has a reading at all. With none, "nothing
  // needs you" would claim more than we know.
  const hasAnyReading = useMemo(
    () => visibleParams.some((p) => byParam[p.key]?.[0]),
    [byParam, visibleParams]
  );
  // "All parameters are tracking in range" only when every graded card has a
  // reading and no card is merely logged: a reef with only alkalinity
  // tested, or a hospital tank in hyposalinity, read as all in range beside
  // a Score card that said "Partial read".
  const allTestsInRange = useMemo(
    () =>
      visibleParams.every((p) =>
        isGradedParam(tank, p.key)
          ? !!currentReadingsClient(byParam[p.key] || [], p.key)[0]
          : !byParam[p.key]?.[0]
      ),
    [byParam, visibleParams, tank]
  );

  // Alert values and ranges in the keeper's own units, the same way the
  // parameter cards show them. Storage stays °F and ppt.
  // An S.G. value prints at the precision of the band beside it, so a HIGH
  // never sits next to a number that reads inside the printed range.
  const sgPlaces = (band, value) => (salinityUnit === 'sg' ? sgDecimals(band, value) : undefined);
  const shownValue = (key, value, band) =>
    key === 'salinity'
      ? `${formatSalinity(value, salinityUnit, sgPlaces(band, value))} ${salinityUnitLabel(salinityUnit)}`
      : key === 'temp' && displayUnits.temp === 'C'
        ? formatTemp(value, 'C')
        : null;
  const shownRange = (key, lo, hi, unit, value) =>
    key === 'salinity'
      ? formatSalinityRange(lo, hi, salinityUnit, sgPlaces([lo, hi], value))
      : key === 'temp' && displayUnits.temp === 'C'
        ? `${formatTemp(lo, 'C', { withUnit: false })}–${formatTemp(hi, 'C')}`
        : `${lo}–${hi}${unit}`;

  // Today's alerts, one row per concern (the old Sensei's update card only
  // named the worst one): a reading flagged as unusual this week, a reading
  // that can no longer be trusted, and every parameter outside its range.
  // A stale reading is never also called high or low: the number can't be
  // trusted as current.
  const todayAlerts = useMemo(() => {
    const out = [];
    const tridentParams = [];
    for (const p of visibleParams) {
      const series = currentReadingsClient(byParam[p.key] || [], p.key);
      const latest = series[0];
      if (!latest) continue;
      const unit = p.unit ? ` ${p.unit}` : '';
      // The keeper's own test outranks the Trident; when the Trident's
      // number disagrees beyond kit error, say so calmly rather than hide
      // it (a reactor running dry shows there first).
      const split = isGradedParam(tank, p.key)
        ? outrankedTridentClient(byParam[p.key] || [], p.key)
        : null;
      if (split) {
        out.push({
          key: `trident-split-${p.key}`,
          paramKey: p.key,
          label: p.label,
          rank: 3,
          info: true,
          title: `${p.label}: your Trident reads ${formatCardValue(p.key, split.trident.value)}${unit}`,
          text: `Your own test read ${formatCardValue(p.key, split.own.value)}${unit}. TankSensei goes by your test, since it cannot tell when the Trident tested. Test again to know which is right.`,
        });
      }
      const value =
        shownValue(p.key, latest.value, p.acceptable) ??
        `${formatCardValue(p.key, latest.value)}${unit}`;
      const wrongParam = implausibleReading(p.key, latest.value, {
        subtypeSlug: tank.subtype_slug,
        automated: isAutomatedMethodClient(latest.method),
      });
      if (wrongParam) {
        out.push({
          key: `implausible-${p.key}`,
          paramKey: p.key,
          label: p.label,
          rank: 0,
          // Stored units on purpose: a ppt value of 1.025 shown on the S.G.
          // scale would read as a normal 1.001 and hide the mistake.
          title: `${p.label} read ${latest.value}${unit}`,
          text: wrongParam.stored || wrongParam.message,
        });
        continue;
      }
      let outlierNote = null;
      // The first ammonia after a run of zeros is always "unusual", and
      // "worth a retest" alone would hide what to do about it, so an
      // off-band ammonia keeps its action; a second off-band reading on the
      // same side confirms an alk / Ca / Mg one, as in the alerts.
      const hold = outlierHold(tank, p, series);
      if (hold?.retestOnly) {
        out.push({
          key: `outlier-${p.key}`,
          paramKey: p.key,
          label: p.label,
          rank: 1,
          title: `${p.label} read ${value}`,
          text: hold.sentence,
        });
        continue;
      }
      if (hold) outlierNote = hold.sentence;
      // A quarantine tank's reef params are logged for the record: an old
      // nitrate there is not something to chase.
      if (!isGradedParam(tank, p.key)) continue;
      const fresh = assessFreshnessClient(series, p.key);
      if (fresh?.reason === 'test_time_unknown') {
        tridentParams.push(p);
        continue;
      }
      if (fresh?.stale) {
        out.push({
          key: `stale-${p.key}`,
          paramKey: p.key,
          label: p.label,
          rank: 2,
          title: `${p.label}: no reading to trust`,
          text: staleConcernText([{ key: p.key, p, reason: fresh.reason }]),
        });
        continue;
      }
      if (paramStatusFor(tank, p.key, latest.value) !== 'off') continue;
      const [lo, hi] = p.acceptable || [];
      if (typeof lo !== 'number' || typeof hi !== 'number') continue;
      const direction = latest.value < lo ? 'low' : 'high';
      // The Trident disagrees with this test (the row above says so): no
      // DOSE until a second test settles which is right.
      const action = split
        ? 'Your Trident reads differently, so test again before acting on this.'
        : actionableUpdateFor(
            p.key,
            latest.value,
            p,
            direction,
            tank,
            computeTrend(series, p.key),
            { salinityUnit, tempUnit: displayUnits.temp }
          );
      out.push({
        key: `off-${p.key}`,
        paramKey: p.key,
        label: p.label,
        rank: 0,
        weight: Math.abs(latest.value - (direction === 'low' ? lo : hi)) / (p.target || 1),
        title: `${p.label} is ${direction}: ${value}`,
        text:
          (lo === hi && p.key !== 'salinity' && p.key !== 'temp'
            ? `Your target is ${lo}${unit}. ${action}`
            : `${endSentence(`Your range is ${shownRange(p.key, lo, hi, unit, latest.value)}`)} ${action}`) +
          (outlierNote ? ` ${outlierNote}` : ''),
      });
    }
    // Trident numbers with no test time are ours to fix, not the keeper's
    // problem: one calm line for all of them, never "no reading to trust",
    // and it does not stand in the way of "Nothing else needs you today".
    if (tridentParams.length) {
      out.push({
        key: 'trident-time',
        paramKey: tridentParams[0].key,
        label: 'Trident',
        rank: 3,
        info: true,
        title: `Trident: ${tridentParams.map((p) => p.label).join(', ')}`,
        text: "The display can't send when your Trident ran these tests yet, so TankSensei shows the numbers but does not grade or alert on them, and asks before a dose. A test you log by hand counts straight away.",
      });
    }
    return out.sort((a, b) => a.rank - b.rank || (b.weight || 0) - (a.weight || 0));
  }, [byParam, visibleParams, tank, salinityUnit]);
  const todayInfoOnly = todayAlerts.every((a) => a.info);

  // Open follow-ups (care plans) for this tank. They feed the "open" row in
  // Today and decide which tasks are a plan's own "Follow up:" task.
  const [followUps, setFollowUps] = useState([]);
  // Which tank the follow-ups were last loaded for (null until they are).
  const [followUpsFor, setFollowUpsFor] = useState(null);
  const followUpsTank = useRef(tank.id);
  // The tank whose last follow-ups load failed: asked again on its next
  // refresh (below), or its one-off tasks could never be ticked here.
  const followUpsFailed = useRef(null);
  const loadFollowUps = () => {
    const id = tank.id;
    followUpsTank.current = id;
    return api(`/api/tanks/${id}/care/plans`)
      .then((d) => {
        if (followUpsTank.current !== id) return;
        followUpsFailed.current = null;
        setFollowUps(openFollowUps(d?.plans));
        setFollowUpsFor(id);
      })
      .catch(() => {
        if (followUpsTank.current === id) followUpsFailed.current = id;
      });
  };
  useEffect(() => {
    setFollowUps([]);
    loadFollowUps();
  }, [tank.id]);
  const followUpPlanByTask = useMemo(() => followUpPlanIdByTask(followUps), [followUps]);

  // HYDROS controllers connected with a key but on none of the keeper's
  // tanks (feedback #69): their readings go nowhere, and nothing else says
  // so. The owner's to fix; a shared tank's keeper never sees it.
  const [unlinkedHydros, setUnlinkedHydros] = useState([]);
  const ownsTank = !tank.role || tank.role === 'owner';
  // A viewer's tick, "Log it" and "Done" were refused by the server and
  // failed in silence; they are the owner's and editors' to use.
  const canAct = tank.role !== 'viewer';
  useEffect(() => {
    if (!ownsTank) {
      setUnlinkedHydros([]);
      return undefined;
    }
    let live = true;
    const load = () =>
      api('/api/hydros/unlinked')
        .then((d) => live && setUnlinkedHydros(Array.isArray(d?.controllers) ? d.controllers : []))
        .catch(() => {});
    load();
    window.addEventListener(HYDROS_CHANGED_EVENT, load);
    return () => {
      live = false;
      window.removeEventListener(HYDROS_CHANGED_EVENT, load);
    };
  }, [tank.id, ownsTank]);

  // A display on this tank that stopped reporting (owner, 2026-10-14). The
  // power-loss pushes back off after a day and stop after a week, so Today
  // keeps one calm row until it reports again or is removed.
  const [tankDevices, setTankDevices] = useState([]);
  useEffect(() => {
    let live = true;
    setTankDevices([]);
    const load = () =>
      api(`/api/tanks/${tank.id}/devices`)
        .then((d) => live && setTankDevices(Array.isArray(d?.devices) ? d.devices : []))
        .catch(() => {});
    load();
    // A display plugged back in while the phone was put away: the row goes
    // when the keeper comes back to the app, not on the next full reload.
    const onShow = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', onShow);
    return () => {
      live = false;
      document.removeEventListener('visibilitychange', onShow);
    };
  }, [tank.id]);
  const quietDevices = tankDevices
    .map((d) => ({ id: d.id, row: quietDeviceRow(d, { owner: ownsTank }) }))
    .filter((d) => d.row);

  // Active quarantine and treatment courses, for their row in Today and the
  // badge on each resident in one.
  const [courses, setCourses] = useState([]);
  const [coursesFor, setCoursesFor] = useState(null);
  const coursesTank = useRef(tank.id);
  const coursesFailed = useRef(null);
  const loadCourses = () => {
    const id = tank.id;
    coursesTank.current = id;
    return api(`/api/tanks/${id}/treatments/active`)
      .then((d) => {
        if (coursesTank.current !== id) return;
        coursesFailed.current = null;
        setCourses(Array.isArray(d?.courses) ? d.courses : []);
        setCoursesFor(id);
      })
      .catch(() => {
        if (coursesTank.current === id) coursesFailed.current = id;
      });
  };
  useEffect(() => {
    setCourses([]);
    loadCourses();
  }, [tank.id]);
  // A list that failed to load is asked again with the tank's next refresh
  // (every action, and the two-minute beat).
  useEffect(() => {
    if (followUpsFailed.current === tank.id) loadFollowUps();
    if (coursesFailed.current === tank.id) loadCourses();
  }, [tank.tasks]);
  // A course's daily check opens the course, where the keeper logs what
  // they saw; ticking it off here would record nothing.
  const courseTaskIds = useMemo(
    () => new Set(courses.map((c) => c.task_id).filter(Boolean)),
    [courses]
  );
  const residentCareKinds = useMemo(() => {
    const out = {};
    for (const c of courses) for (const id of c.livestock_ids || []) out[id] ||= c.kind;
    return out;
  }, [courses]);

  // Until its list has loaded, a task that may be a follow-up's (no cadence:
  // completing it deletes it and orphans the plan) or a course's daily check
  // (ticking it records nothing) cannot be ticked from here.
  const followUpsLoaded = followUpsFor === tank.id;
  const coursesLoaded = coursesFor === tank.id;
  // A viewer may open a follow-up or a course, never tick a task off.
  const tickLocked = (item) =>
    item.unsure || (!canAct && !item.followUpPlanId && !courseTaskIds.has(item.id));

  // Today's plan — overdue/due-today tasks + critical supplies.
  const plan = useMemo(() => {
    const items = [];
    for (const t of tank.tasks || []) {
      const followUpPlanId = followUpPlanByTask.get(t.id) || null;
      // A lapsed task waits on the Tasks page for a keep / change / remove
      // answer instead of sitting in today's plan forever. A follow-up has
      // no cadence and would lapse too, but stays until its plan is closed.
      if (isTaskLapsed(t) && !followUpPlanId) continue;
      const d = daysFromNow(t.due);
      if (d != null && d <= 0)
        items.push({
          kind: 'task',
          id: t.id,
          title: t.title,
          sub: d < 0 ? `Due ${-d}d ago` : 'Due today',
          overdue: d < 0,
          followUpPlanId,
          unsure:
            (!followUpsLoaded && !followUpPlanId && !(Number(t.cadence_days) > 0)) ||
            (!coursesLoaded && COURSE_CHECK_TITLE.test(t.title || '')),
        });
    }
    const supplyItems = (tank.supplies || [])
      .map((s) => ({ ...s, f: supplyForecast(s) }))
      .filter((s) => s.f.status === 'critical');
    // RO/DI cartridges (legacy schema) + equipment_parts (UV bulbs,
    // probes, impellers…) both feed the same runway. Past-due and
    // ≤7-day parts land in Today's Plan next to a salt bucket that's
    // out. Reorder URL rides on the item so the row can carry a
    // one-tap link when present.
    const cartridgeItems = equipmentSupplyItems(tank).filter((s) => s.f.status === 'critical');
    const partItems = equipmentPartItems(tank, config?.equipment).filter(
      (s) => s.f.status === 'critical'
    );
    for (const s of [...supplyItems, ...cartridgeItems, ...partItems]) {
      items.push({
        kind: 'supply',
        id: s.id,
        title: `Reorder ${s.name.split(' (')[0]}`,
        reorder_url: s.reorder_url || null,
        // Resolver inputs: only equipment_parts items carry these.
        // Supplies + RO/DI cartridge items fall back to the "Review"
        // button when reorder_url is empty.
        part_kind: s.part_kind || null,
        brand: s.brand || null,
        sub: s.f.label,
      });
    }
    // Phase 5 — gear-specific maintenance due (clean fans, soak
    // impeller, recalibrate probe). Maintenance is upkeep, not a
    // reorder: the row gets a "Done" action, no retailer link.
    const serviceItems = equipmentServiceItems(tank, config?.equipment).filter(
      (s) => s.f.status === 'critical'
    );
    for (const s of serviceItems) {
      items.push({
        kind: 'service',
        id: s.id,
        part_id: s.part_id,
        title: s.action,
        sub: s.f.label,
      });
    }
    return items;
  }, [
    tank.tasks,
    tank.supplies,
    tank.equipment,
    config?.equipment,
    followUpPlanByTask,
    followUpsLoaded,
    coursesLoaded,
  ]);

  const [completing, setCompleting] = useState(null);
  const [showWaterChange, setShowWaterChange] = useState(false);
  const completeTask = async (taskId) => {
    setCompleting(taskId);
    try {
      await api(`/api/tanks/${tank.id}/tasks/${taskId}/complete`, { method: 'POST' });
      await onReload();
    } catch {
      // A task that is already gone (its course ended in another tab)
      // just needs the list refreshed.
      await onReload().catch(() => {});
    } finally {
      setCompleting(null);
    }
  };
  // Mark a gear-maintenance item serviced — rolls last_serviced_at to
  // today so the next service-due date moves out by service_every_days.
  const markServiced = async (partId, planId) => {
    setCompleting(planId);
    try {
      await api(`/api/tanks/${tank.id}/equipment/parts/${partId}/serviced`, {
        method: 'POST',
        body: { day: localDateString() },
      });
      await onReload();
    } catch {
      await onReload().catch(() => {});
    } finally {
      setCompleting(null);
    }
  };

  // Inline per-card quick-add. Only one card is in quick-add mode at a time.
  //   - manual: number in, Save out. Kit name is optional.
  //   - hanna / strip / refract: tapping the method pill opens the camera
  //     right away; on photo, server OCR prefills the value; user verifies
  //     and Save writes the reading with method + confidence + note + kit.
  // Method lists per param come from app_config.capture_presets.methods_for_param;
  // a per-param override (e.g. salinity → [manual, refract]) wins, otherwise
  // built-ins use default_builtin and custom params use default_custom.
  const METHODS_FOR = (paramKey) => {
    const p = effectiveParam(tank, paramKey);
    // Ammonia and nitrite cards are added as custom cards, but a strip
    // photo reads them (feedback #79), so they take the built-in list.
    const known = !p?.custom || photoReadable(paramKey);
    let keys = methodsForCfg[paramKey];
    if (!Array.isArray(keys)) {
      keys = known ? methodsForCfg.default_builtin : methodsForCfg.default_custom;
    }
    if (!Array.isArray(keys) || !keys.length) keys = ['manual'];
    // Only the photo methods that read the test: the Hanna pill on nitrite
    // would save the ULR checker's ppb as ppm, and a strip pill on
    // temperature reads nothing (review of feedback #79).
    if (known) keys = keys.filter((k) => photoMethodReads(k, paramKey));
    if (!keys.length) keys = ['manual'];
    return keys.map((k) => ({ k, label: METHOD_LABELS[k] || k }));
  };
  const [quickAdd, setQuickAdd] = useState(null); // full state below
  const fileRef = useRef(null);
  const openQuickAdd = (param) =>
    setQuickAdd({
      param,
      value: '',
      method: 'manual',
      kit: '',
      analyzing: false,
      confidence: null,
      note: '',
      photoUrl: null,
      saving: false,
      error: '',
      // For salinity only, carry the input-unit choice. Other params
      // ignore this field.
      inputUnit: param === 'salinity' ? readSalinityUnit() : null,
    });
  const closeQuickAdd = () => {
    if (quickAdd?.photoUrl) URL.revokeObjectURL(quickAdd.photoUrl);
    setQuickAdd(null);
  };
  // One hidden input serves both paths: `capture` forces the camera on
  // phones; removing it lets the OS offer the photo library instead.
  const openCamera = () => {
    if (fileRef.current) {
      fileRef.current.setAttribute('capture', 'environment');
      fileRef.current.click();
    }
  };
  const openGallery = () => {
    if (fileRef.current) {
      fileRef.current.removeAttribute('capture');
      fileRef.current.click();
    }
  };
  // One tap instead of two: picking Hanna/Strip/Refract opens the camera.
  // Picking Manual clears any photo state and focuses the input path.
  const pickMethod = (method) => {
    if (quickAdd?.photoUrl) URL.revokeObjectURL(quickAdd.photoUrl);
    setQuickAdd(
      (q) =>
        q && {
          ...q,
          method,
          confidence: null,
          note: '',
          photoUrl: null,
          error: '',
          outlierPrompt: null,
        }
    );
    if (method !== 'manual') {
      setTimeout(openCamera, 0);
    }
  };
  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !quickAdd) return;
    if (quickAdd.photoUrl) URL.revokeObjectURL(quickAdd.photoUrl);
    const photoUrl = URL.createObjectURL(file);
    // A new photo is a new number: the last one's "Are you sure?" must not
    // carry over and save it unasked.
    setQuickAdd(
      (q) =>
        q && {
          ...q,
          photoUrl,
          analyzing: true,
          error: '',
          note: '',
          confidence: null,
          outlierPrompt: null,
          moveToSalinity: null,
          salRead: null,
        }
    );
    try {
      // Canvas-first for every format (Safari decodes HEIC natively,
      // so iPhones ship a small JPEG); raw bytes only when this
      // browser can't decode — server normalises those.
      const { body: uploadBody, mime } = await prepareImageUpload(file, {
        maxDim: 1200,
        quality: 0.85,
      });
      const qs = new URLSearchParams({ method: quickAdd.method, param: quickAdd.param }).toString();
      const res = await fetch(`/api/tanks/${tank.id}/readings/analyze?${qs}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': mime },
        body: uploadBody,
      });
      const body = await res.json().catch(() => null);
      if (!res.ok)
        throw new Error(body?.error || `Sensei couldn't read the photo (${res.status}).`);
      setQuickAdd((q) => {
        if (!q) return q;
        let v = body.value;
        let salRead = null;
        // Refractometers carry both scales; Vision may read either
        // column. Normalise the OCR result to canonical ppt (auto-
        // detects an S.G.-scale read like 1.026), then express it in
        // whatever unit the input toggle is set to so the prefilled
        // number matches what the user sees labelled. The ppt is kept:
        // 36.9 ppt shown as S.G. 1.027 would otherwise save as 36.3.
        if (q.param === 'salinity' && v != null) {
          const ppt = salinityToPpt(v, 'ppt');
          // On S.G. at the places the card will show it with: 36.9 ppt is
          // 1.0274, HIGH, where 1.027 reads inside a 33.5-36.5 band.
          v =
            q.inputUnit === 'sg'
              ? formatSalinity(
                  ppt,
                  'sg',
                  sgDecimals(effectiveParam(tank, 'salinity')?.acceptable, ppt)
                )
              : ppt;
          salRead = { shown: String(v), ppt };
        }
        let convertedNote = null;
        if (q.param === 'alk' && v != null) {
          const alk = alkPhotoReadToDkh(v, body.unit, q.method);
          v = alk.value;
          convertedNote = alk.note;
        }
        return {
          ...q,
          analyzing: false,
          salRead,
          value: v != null ? String(v) : q.value,
          confidence: typeof body.confidence === 'number' ? body.confidence : null,
          note: [body.note || '', convertedNote].filter(Boolean).join(' · '),
        };
      });
    } catch (err) {
      setQuickAdd(
        (q) => q && { ...q, analyzing: false, error: err.message || 'Could not analyze.' }
      );
    }
  };
  const saveQuickAdd = async (force = false) => {
    // A second Enter while the first save runs would post it twice.
    if (!quickAdd || quickAdd.saving) return;
    // The question clears when the value changes, so saving again with it
    // still on screen is the keeper's "yes, that is right".
    if (quickAdd.outlierPrompt) force = true;
    const raw = parseFloat(quickAdd.value);
    if (!Number.isFinite(raw)) {
      setQuickAdd((q) => q && { ...q, error: 'Enter a number.' });
      return;
    }
    // Salinity-only S.G. → ppt conversion. Storage is always ppt; this
    // is the input path only. Plausibility checks catch typos like
    // 1.26 instead of 1.026.
    let value = raw;
    if (quickAdd.param === 'salinity') {
      if (!isPlausibleSalinity(raw, quickAdd.inputUnit)) {
        const hint =
          quickAdd.inputUnit === 'sg'
            ? 'S.G. should be between 1.005 and 1.040.'
            : `${salinityUnitLabel(quickAdd.inputUnit)} should be between 5 and 55 (or an S.G. like 1.026).`;
        setQuickAdd((q) => q && { ...q, error: hint });
        return;
      }
      // salinityToPpt also auto-detects scale mismatches (an S.G.-looking
      // number typed while ppt is selected, and vice versa).
      value =
        quickAdd.salRead && quickAdd.value === quickAdd.salRead.shown
          ? quickAdd.salRead.ppt
          : salinityToPpt(raw, quickAdd.inputUnit);
    }
    // Temperature input → storage is canonical °F. When the tank prefers
    // °C, treat the typed number as Celsius and convert. We only do this
    // for the manual-entry path; OCR + HYDROS ingest already emit °F.
    if (quickAdd.param === 'temp' && displayUnits.temp === 'C' && quickAdd.method !== 'hanna') {
      // To 0.1 °F, as Log and the import store it: 25.3 °C kept as 77.54
      // was not seen as the same test when the keeper's sheet came in.
      value = Math.round(((raw * 9) / 5 + 32) * 10) / 10;
    }
    // Phosphate as Hanna Phosphorus (HI-736): the checker reads P in ppb,
    // so the conversion to canonical PO₄ ppm is × 3.066 ÷ 1000 (the bare
    // 3.066 stoichiometric factor PLUS the ppb → ppm unit shift). Storage
    // stays PO₄ so history charts + Sensei score read uniform values. The
    // original ppb number is preserved via kit tag + note so it's
    // recoverable on inspection.
    let extraNoteFromPhosphorus = null;
    let extraKitFromPhosphorus = null;
    if (quickAdd.param === 'phosphate' && quickAdd.phosphateAs === 'p') {
      value = hannaPhosphorusPpbToPo4Ppm(raw);
      extraNoteFromPhosphorus = `Hanna Phosphorus: ${raw} ppb`;
      extraKitFromPhosphorus = 'hanna-phosphorus';
    }
    // A number no kit can read for this parameter (a refractometer's 1.023
    // typed into alkalinity) is never saved; the server refuses it too.
    const implausible = implausibleReading(quickAdd.param, value, {
      subtypeSlug: tank.subtype_slug,
      shown:
        quickAdd.param === 'temp' && displayUnits.temp === 'C' && quickAdd.method !== 'hanna'
          ? `${raw}°C`
          : quickAdd.param === 'salinity'
            ? String(raw)
            : null,
    });
    if (implausible) {
      setQuickAdd(
        (q) =>
          q && {
            ...q,
            error: implausible.message,
            moveToSalinity: implausible.likely === 'salinity' ? String(raw) : null,
          }
      );
      return;
    }
    // "Are you sure?" for a number far outside the keeper's own range,
    // asked before the history-based check below, which needs three past
    // readings and so never fires on a new tank or a first test.
    if (force !== true) {
      const confirmText = confirmPromptFor(tank, quickAdd.param, value, {
        salinityUnit,
        typed: extraNoteFromPhosphorus
          ? `${raw} ppb phosphorus (${Math.round(value * 1000) / 1000} ppm as phosphate)`
          : null,
      });
      if (confirmText) {
        setQuickAdd((q) => q && { ...q, saving: false, error: '', outlierPrompt: confirmText });
        return;
      }
    }
    setQuickAdd((q) => q && { ...q, saving: true, error: '', outlierPrompt: null });
    // Pre-save outlier check (Phase 4): ask BEFORE the row exists, so a
    // fat-fingered 84 instead of 8.4 never poisons the score, the
    // forecast, and the dose baseline in the first place. Advisory: a
    // check failure never blocks the save, and "Save anyway" skips it.
    if (force !== true) {
      try {
        const chk = await api(`/api/tanks/${tank.id}/readings/check`, {
          method: 'POST',
          body: { param: quickAdd.param, value },
        });
        if (chk?.outlier_reason) {
          setQuickAdd(
            (q) =>
              q && {
                ...q,
                saving: false,
                outlierPrompt: `Unusual reading: ${outlierMessage(chk, tank)} Double-check the test before saving.`,
              }
          );
          return;
        }
      } catch {
        /* advisory only */
      }
    }
    try {
      const body = {
        param: quickAdd.param,
        value,
        unit: effectiveParam(tank, quickAdd.param)?.unit || '',
        method: quickAdd.method,
      };
      if (quickAdd.kit && quickAdd.kit.trim()) body.kit = quickAdd.kit.trim();
      else if (extraKitFromPhosphorus) body.kit = extraKitFromPhosphorus;
      if (quickAdd.confidence != null) body.confidence = Math.round(quickAdd.confidence * 100);
      const userNote = quickAdd.note ? quickAdd.note.trim() : '';
      const noteBits = [extraNoteFromPhosphorus, userNote].filter(Boolean);
      if (noteBits.length) body.note = noteBits.join(' · ');
      await api(`/api/tanks/${tank.id}/readings`, { method: 'POST', body });
    } catch (e) {
      setQuickAdd((q) => q && { ...q, saving: false, error: e.message || 'Could not save.' });
      return;
    }
    track('reading.save', 'home', { param: quickAdd.param, method: quickAdd.method });
    // Saved: a refresh that fails after it must not read "Could not save.",
    // which a keeper retried into a second copy. The heartbeat refreshes.
    try {
      await onReload();
    } catch {
      /* shown on the next refresh */
    }
    closeQuickAdd();
  };

  const readingsCount = (tank.readings || []).length;
  const livestockCount = (tank.livestock || []).length;
  // A viewer can do none of its steps (log, add livestock, tank settings).
  const showFirstRun = readingsCount === 0 && tank.role !== 'viewer';

  const [proMode, setProMode] = useState(() => {
    try {
      return localStorage.getItem(PRO_MODE_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(PRO_MODE_KEY, proMode ? '1' : '0');
    } catch {
      /* private mode — non-fatal */
    }
  }, [proMode]);

  // Parameter-grid column preference, per device. '2' forces two
  // columns even on a phone (some keepers prefer the denser grid);
  // anything else keeps the responsive default (1 on phones, 2 on
  // tablets, 3-4 on wide desktops).
  const [twoColParams, setTwoColParams] = useState(() => {
    try {
      return localStorage.getItem(PARAM_COLS_KEY) === '2';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(PARAM_COLS_KEY, twoColParams ? '2' : '1');
    } catch {
      /* private mode — non-fatal */
    }
  }, [twoColParams]);

  // Per-user Home layout. The user drags sections around in Edit-layout
  // mode (pointer drag from the grip pill; arrow keys on the focused
  // handle for keyboard users) and the arrangement persists to
  // users.home_layout server-side (via PUT /api/auth/me/home-layout)
  // so it follows them across desktop, mobile, and a fresh install.
  // localStorage stays as a fast first-paint cache that warms before
  // /api/auth/me lands. mergeLayout drops unknown saved keys and
  // appends missing ones so a new section in a future build shows up
  // in default position rather than disappearing for old users.
  const [layoutRaw, setLayoutRaw] = useState(() =>
    pickInitialLayout({
      server: user?.home_layout,
      session: layoutSync.session?.userId === user?.id ? layoutSync.session : null,
      cached: readCachedLayout(),
    })
  );
  const { order: layout, hidden: hiddenSections } = decodeLayout(layoutRaw);
  const show = (key) => !hiddenSections.has(key);
  const [editingLayout, setEditingLayout] = useState(false);
  // Tests show as one line each; the one tapped opens as its full card.
  // A test reading HIGH or LOW stays a full card (Today says "Tap DOSE on
  // the alkalinity card") until the keeper taps Show less. Edit layout
  // shows every card, for dragging.
  const [openParam, setOpenParam] = useState(null);
  // Per test: the reading its HIGH / LOW card was closed on.
  const [closedParams, setClosedParams] = useState({});
  // Where focus goes once a row opens or a card closes ('card:alk' /
  // 'row:alk'): the button tapped is gone from the page.
  const focusParamRef = useRef(null);
  const compactParams = !editingLayout;
  const gridRef = useRef(null);
  const layoutSnapshotRef = useRef(null);
  // If the server-side layout changes after mount (another device saved
  // and the user was reloaded), adopt it so the
  // screen reflects whatever the user last saved anywhere.
  const serverLayoutKey = layoutKey(user?.home_layout);
  const seenServerKey = useRef(serverLayoutKey);
  useEffect(() => {
    if (serverLayoutKey === seenServerKey.current) return;
    seenServerKey.current = serverLayoutKey;
    if (!Array.isArray(user?.home_layout)) return;
    // A newer save from this tab is still on its way up, so this copy is
    // older than what is on screen.
    if (layoutSync.inFlight > 0) {
      if (layoutSync.session) layoutSync.session.serverKey = serverLayoutKey;
      return;
    }
    setLayoutRaw(user.home_layout);
    layoutSync.session = {
      userId: user.id,
      layout: user.home_layout,
      serverKey: serverLayoutKey,
    };
  }, [serverLayoutKey]);
  useEffect(() => {
    try {
      localStorage.setItem(LAYOUT_KEY, JSON.stringify(encodeLayout(layout, hiddenSections)));
    } catch {
      /* private mode — non-fatal */
    }
  }, [layoutRaw]);
  // Push every change up to the server. The next mount starts from
  // layoutSync, not a refreshed user: App's refreshMe signs the keeper out
  // on any error, so a network blip after moving a section would land
  // them on the sign-in screen. A failed PUT keeps the change on this
  // device (localStorage + layoutSync); it just won't reach the others
  // until the next successful save.
  const persistLayoutToServer = (next) => {
    layoutSync.session = { userId: user?.id, layout: next, serverKey: seenServerKey.current };
    layoutSync.inFlight += 1;
    api('/api/auth/me/home-layout', { method: 'PUT', body: { layout: next } })
      .catch(() => {})
      .finally(() => {
        layoutSync.inFlight -= 1;
      });
  };
  const orderOf = (key) => {
    const i = layout.indexOf(key);
    return i === -1 ? DEFAULT_LAYOUT.indexOf(key) : i;
  };
  const saveLayout = (order, hidden) => {
    const next = encodeLayout(order, hidden);
    setLayoutRaw(next);
    persistLayoutToServer(next);
  };
  // Arrow keys step past hidden sections, so every press visibly moves.
  const moveSection = (key, dir) => {
    const next = layout.slice();
    const i = next.indexOf(key);
    let j = i + dir;
    while (j >= 0 && j < next.length && hiddenSections.has(next[j])) j += dir;
    if (i < 0 || j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    saveLayout(next, hiddenSections);
  };
  const hideSection = (key) => {
    const hidden = new Set(hiddenSections);
    hidden.add(key);
    saveLayout(layout, hidden);
  };
  const showSection = (key) => {
    const hidden = new Set(hiddenSections);
    hidden.delete(key);
    saveLayout(layout, hidden);
  };
  // The tray offers only what would show once added back: the Score and
  // the Welcome card sit out Pro mode, the Welcome card needs a tank with
  // no readings, and the controller card needs a linked HYDROS.
  const sectionCanRender = (k) =>
    k === 'score'
      ? !proMode
      : k === 'welcome'
        ? showFirstRun && !proMode
        : k === 'controller'
          ? Boolean(tank?.hydros_thing_id)
          : k === 'tip'
            ? showsReefFact(tank)
            : true;
  const hiddenOffered = layout.filter((k) => hiddenSections.has(k) && sectionCanRender(k));
  const resetLayout = () => {
    const next = DEFAULT_LAYOUT.slice();
    setLayoutRaw(next);
    persistLayoutToServer(next);
  };
  const startSectionDrag = (e, key) => {
    layoutSnapshotRef.current = encodeLayout(layout, hiddenSections);
    startReorderDrag({
      event: e,
      container: gridRef.current,
      key,
      label: SECTION_LABELS[key],
      onPreview: (dragKey, overKey) =>
        setLayoutRaw((prev) => {
          const d = decodeLayout(prev);
          return encodeLayout(reorderPreview(d.order, dragKey, overKey), d.hidden);
        }),
      // Preview already landed in state and the localStorage effect
      // persists it locally — commit fires the cross-device server save
      // with the final arrangement.
      onCommit: () => {
        setLayoutRaw((latest) => {
          if (latest) {
            const d = decodeLayout(latest);
            persistLayoutToServer(encodeLayout(d.order, d.hidden));
          }
          return latest;
        });
      },
      onCancel: () => setLayoutRaw(layoutSnapshotRef.current || DEFAULT_LAYOUT.slice()),
    });
  };

  // Param-card reorder (inside the PARAMETERS section, edit mode only).
  // Optimistic: paramKeyOrder drives the render immediately; the PATCH
  // re-spaces every visible param's `order` field (10, 20, 30…) on the
  // tank so the arrangement persists server-side and syncs across
  // devices — unlike the section layout, which is per-device.
  const [paramKeyOrder, setParamKeyOrder] = useState(null);
  useEffect(() => setParamKeyOrder(null), [tank.id]);
  const paramGridRef = useRef(null);
  const paramSnapshotRef = useRef(null);
  const orderedParams = useMemo(() => {
    if (!paramKeyOrder) return visibleParams;
    const byKey = new Map(visibleParams.map((p) => [p.key, p]));
    const out = paramKeyOrder.map((k) => byKey.get(k)).filter(Boolean);
    for (const p of visibleParams) if (!paramKeyOrder.includes(p.key)) out.push(p);
    return out;
  }, [visibleParams, paramKeyOrder]);
  const persistParamOrder = (keys) => {
    const params = {};
    keys.forEach((k, idx) => {
      params[k] = { order: (idx + 1) * 10 };
    });
    api(`/api/tanks/${tank.id}/params`, { method: 'PATCH', body: { params } }).catch(() => {
      // Server didn't take it — drop the optimistic order so the UI
      // falls back to the persisted arrangement instead of lying.
      setParamKeyOrder(null);
    });
  };
  const moveParam = (key, dir) => {
    const keys = orderedParams.map((p) => p.key);
    const i = keys.indexOf(key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= keys.length) return;
    [keys[i], keys[j]] = [keys[j], keys[i]];
    setParamKeyOrder(keys);
    persistParamOrder(keys);
  };
  const startParamDrag = (e, key, label) => {
    paramSnapshotRef.current = paramKeyOrder;
    startReorderDrag({
      event: e,
      container: paramGridRef.current,
      key,
      label,
      onPreview: (dragKey, overKey) => {
        setParamKeyOrder((prev) => {
          const keys = prev ? prev.slice() : orderedParams.map((p) => p.key);
          return reorderPreview(keys, dragKey, overKey);
        });
      },
      onCommit: () => {
        // Read the freshest order out of state — the previews landed
        // there during the drag.
        setParamKeyOrder((latest) => {
          if (latest) persistParamOrder(latest);
          return latest;
        });
      },
      onCancel: () => setParamKeyOrder(paramSnapshotRef.current),
    });
  };

  // Resident-card reorder lives inside LivestockResidents; it just
  // needs to know we're in edit mode.

  return (
    <div
      ref={gridRef}
      className={`home-grid${editingLayout ? ' editing' : ''}`}
      style={{ padding: '0 20px' }}
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/*,.heic,.heif"
        onChange={onPhoto}
        style={{ display: 'none' }}
      />
      {/* Reachability is the exception to the nags-never-first rule —
          if no channel can reach the keeper, that outranks everything
          on Home (explicit product call). order -1 pins it to the top. */}
      <NotificationNudgeBanner onOpenAccount={onOpenAccount} order={-1} />

      {/* Below the live content (score / photos / update), above the
          params grid — a nag should never be the first thing on Home. */}
      <EmailChangeNote user={user} onOpenAccount={onOpenAccount} order={orderOf('params')} />
      <SetPasswordBanner
        hasPassword={user?.has_password}
        onDone={onRefreshUser}
        order={orderOf('params')}
      />

      {/* Edit layout and Pro mode sit at the foot of Home, out of the way of
          what needs the keeper; while editing, the bar comes to the top so
          Done is in reach (owner, 2026-10-21). */}
      <div className="hv-pro-bar" style={{ order: editingLayout ? -1 : 9999 }}>
        {editingLayout ? (
          <>
            <label
              className="hv-pro-toggle"
              title="Show parameter cards in two columns instead of one"
            >
              <input
                type="checkbox"
                checked={twoColParams}
                onChange={(e) => setTwoColParams(e.target.checked)}
              />
              <span>2-col params</span>
            </label>
            <button
              type="button"
              className="hv-layout-link font-heading"
              onClick={resetLayout}
              title="Reset to the default Home layout"
            >
              Reset
            </button>
            <button
              type="button"
              className="hv-layout-done font-heading"
              onClick={() => setEditingLayout(false)}
            >
              Done
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="hv-layout-link font-heading"
              onClick={() => {
                setEditingLayout(true);
                // The bar, with Done, moves to the top and every card opens:
                // from the foot of Home the keeper was left mid-page.
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              title="Rearrange the Home sections"
            >
              Edit layout
            </button>
            <label className="hv-pro-toggle" title="Hide welcome card and Sensei Score banner">
              <input
                type="checkbox"
                checked={proMode}
                onChange={(e) => setProMode(e.target.checked)}
              />
              <span>Pro mode</span>
            </label>
          </>
        )}
      </div>

      {editingLayout && hiddenOffered.length > 0 && (
        <div className="hv-hidden-tray span-2">
          <span className="hv-hidden-tray-label font-heading">Hidden from Home</span>
          {hiddenOffered.map((k) => (
            <button
              key={k}
              type="button"
              className="hv-hidden-add font-heading"
              onClick={() => showSection(k)}
            >
              + {SECTION_LABELS[k]}
            </button>
          ))}
        </div>
      )}

      {!proMode && show('score') && (
        <div
          className="hv-cell hv-cell-score span-2"
          data-drag-key="score"
          style={{ order: orderOf('score') }}
        >
          {editingLayout && (
            <LayoutChrome
              k="score"
              onDragStart={startSectionDrag}
              move={moveSection}
              onHide={hideSection}
            />
          )}
          <SenseiScore tank={tank} onTab={onTab} onSetEstablishedDate={onOpenTankSettings} />
        </div>
      )}

      {/* Live HYDROS controller card. Renders nothing when no controller
         is linked — so the slot is invisible (and unreorderable in the
         layout grid) for users without a controller. */}
      {tank?.hydros_thing_id && show('controller') && (
        <div
          className="hv-cell hv-cell-controller span-2"
          data-drag-key="controller"
          style={{ order: orderOf('controller') }}
        >
          {editingLayout && (
            <LayoutChrome
              k="controller"
              onDragStart={startSectionDrag}
              move={moveSection}
              onHide={hideSection}
            />
          )}
          <HydrosLiveTile tank={tank} />
        </div>
      )}

      {/* Stays visible in Pro mode — it's tank data, not hand-holding. */}
      {show('photos') && (
        <div
          className="hv-cell hv-cell-photos span-2"
          data-drag-key="photos"
          style={{ order: orderOf('photos') }}
        >
          {editingLayout && (
            <LayoutChrome
              k="photos"
              onDragStart={startSectionDrag}
              move={moveSection}
              onHide={hideSection}
            />
          )}
          <PhotoTimeline tank={tank} />
        </div>
      )}

      {showFirstRun && !proMode && show('welcome') && (
        <div
          className="hv-cell hv-cell-welcome span-2"
          data-drag-key="welcome"
          style={{ order: orderOf('welcome') }}
        >
          {editingLayout && (
            <LayoutChrome
              k="welcome"
              onDragStart={startSectionDrag}
              move={moveSection}
              onHide={hideSection}
            />
          )}
          <FirstRunCard
            tank={tank}
            readingsCount={readingsCount}
            livestockCount={livestockCount}
            onLog={() => onTab('log')}
            onBrowseLivestock={onOpenLivestock}
            onOpenTankSettings={onOpenTankSettings}
          />
        </div>
      )}

      {show('params') && (
        <section
          className="slide-in span-2 hv-cell hv-cell-params"
          data-drag-key="params"
          style={{ order: orderOf('params') }}
        >
          {editingLayout && (
            <LayoutChrome
              k="params"
              onDragStart={startSectionDrag}
              move={moveSection}
              onHide={hideSection}
            />
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              rowGap: 8,
              marginBottom: 10,
            }}
          >
            <div
              className="font-heading hv-section-title"
              style={{ fontSize: 17, letterSpacing: '0.08em' }}
            >
              PARAMETERS
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {onOpenAllHistory ? (
                <button
                  type="button"
                  onClick={onOpenAllHistory}
                  className="font-heading hv-header-chip"
                  style={HEADER_CHIP_STYLE}
                >
                  All history
                </button>
              ) : null}
              {onOpenImport ? (
                <button
                  type="button"
                  onClick={onOpenImport}
                  className="font-heading hv-header-chip"
                  style={HEADER_CHIP_STYLE}
                >
                  Import tests
                </button>
              ) : null}
              {onOpenIcp ? (
                <button
                  type="button"
                  onClick={onOpenIcp}
                  className="font-heading hv-header-chip"
                  style={HEADER_CHIP_STYLE}
                >
                  ICP results
                </button>
              ) : null}
            </div>
          </div>
          <CycleCardsNote key={tank.id} tank={tank} onReload={onReload} />
          <div
            ref={paramGridRef}
            className={`hv-param-grid${twoColParams ? ' hv-param-grid--two' : ''}${
              compactParams ? ' hv-param-grid--rows' : ''
            }`}
          >
            {orderedParams.map((p) => {
              const k = p.key;
              // A recent hand test speaks for the tank before Trident rows.
              const readings = currentReadingsClient(byParam[k] || [], k);
              const latest = readings[0];
              const st = latest ? paramStatusFor(tank, k, latest.value) : 'unknown';
              // Freshness check: SILENT (no new data for the source's
              // expected cadence) OR FROZEN (probe still posting but the
              // value never moves — dead Trident, Apex republishing the
              // last result). Either way the STALE pill replaces
              // OK/HIGH/LOW and the value is treated as untrustworthy.
              const freshness = assessFreshnessClient(readings, k);
              // A QT's logged-only params are never "too old to trust": they
              // are not graded at all, so LOGGED stands.
              const untrusted = !!freshness?.stale && isGradedParam(tank, k);
              const staleReason = freshness?.reason || null;
              // SOFT doubt: the value has not moved across several readings
              // spanning months. Unlike the three stale reasons above this
              // does NOT void the reading — a coarse test kit can honestly
              // repeat a number — so the status pill and the Score are
              // unchanged. We only ask the keeper to confirm it is current.
              const suspectUnchanged = !untrusted && freshness?.suspect_reason === 'unchanged';
              const untrustedDays = untrusted ? daysSinceReading(latest?.ts) : null;
              // Status pill — direction-aware so a 12.2 dKH alk reads "HIGH",
              // not just "OFF". TBD covers two distinct states with two
              // tooltips: no reading logged yet, and a reading on a param
              // with no acceptable band (paramStatusFor → 'unknown') —
              // the old single "No reading yet" tooltip contradicted the
              // value visibly rendered on the same card.
              // A Trident number is ours to date, not the keeper's to doubt:
              // its own word, in the calm tone (shared/trident-trust.js).
              const tridentTime = untrusted && staleReason === 'test_time_unknown';
              const pillLabel = !latest
                ? 'TBD'
                : tridentTime
                  ? 'TEST TIME?'
                  : untrusted
                    ? 'STALE'
                    : st === 'off' && p.acceptable
                      ? latest.value > p.acceptable[1]
                        ? 'HIGH'
                        : 'LOW'
                      : st === 'ok'
                        ? 'OK'
                        : st === 'tracked'
                          ? isCycleQuiet(tank, k)
                            ? 'CYCLING'
                            : 'LOGGED'
                          : st === 'implausible'
                            ? 'CHECK'
                            : 'TBD';
              const pillTone = !latest
                ? 'tbd'
                : tridentTime
                  ? 'tbd'
                  : untrusted
                    ? 'stale'
                    : st === 'tracked'
                      ? 'tbd'
                      : st === 'implausible'
                        ? 'stale'
                        : st;
              const stalePillTitle =
                staleReason === 'stuck'
                  ? `Value has been EXACTLY the same across many readings in the last 24 h — no jitter at all. That's the signature of a stuck probe, a calibration-locked test kit, or a source that's stopped reading reality. Sensei has stopped counting this toward your Score. Log a fresh test (or check your probe) to confirm.`
                  : staleReason === 'frozen'
                    ? `Value hasn't moved in days — your tester likely stopped testing while the controller keeps republishing the last result. Sensei has stopped counting this toward your Score. Log a fresh test to confirm.`
                    : staleReason === 'test_time_unknown'
                      ? `The display can't send when your Trident ran this test yet, so it may be an old result. TankSensei shows it but does not count it toward your Score or alert on it. A test you log by hand counts straight away.`
                      : `No new readings for ${untrustedDays ?? 'days'} days — too old to trust. Sensei has stopped counting this toward your Score. Log a fresh test.`;
              const pillTitle =
                pillLabel === 'STALE' || pillLabel === 'TEST TIME?'
                  ? stalePillTitle
                  : pillLabel === 'OK' || pillLabel === 'HIGH' || pillLabel === 'LOW'
                    ? pillRangeTitle(pillLabel, k, p.acceptable, p.unit, {
                        salinityUnit,
                        tempUnit: displayUnits.temp,
                      })
                    : pillLabel === 'CHECK'
                      ? implausibleReading(k, latest.value, { subtypeSlug: tank.subtype_slug })
                          ?.message
                      : pillLabel === 'CYCLING'
                        ? 'The tank is cycling: this rising and falling is the cycle working, so it is charted, not called high or alerted. Once a fish, coral or invert is listed in this tank, or the cycle reads done, it alerts if it reads above zero.'
                        : pillLabel === 'LOGGED'
                          ? k === 'salinity'
                            ? 'Quarantine tank: salinity is logged, not graded, because it may be low on purpose (hyposalinity).'
                            : 'Quarantine tank: logged for your records, not graded against a reef range.'
                          : latest
                            ? 'No acceptable range set — edit this parameter in Tank settings to enable status'
                            : 'No reading yet';
              const cardTitle = suspectUnchanged
                ? `${pillTitle}\n\nThis number has not moved across your last several tests, spanning months. It still counts toward your Score, but a value that never changes is often one being carried forward rather than re-measured. Log a fresh test to confirm it.`
                : pillTitle;
              const history = readings
                .slice(0, 14)
                .reverse()
                .map((r) => r.value);
              // Directional trend for the last 24 h — distinct from the
              // status pill: a value can read OK while sliding toward an
              // edge. The card surfaces just the DIRECTION inline with
              // the value (↑ / ↓ / → for flat / nothing if there isn't
              // enough data); the per-day rate lives in the history
              // sheet for anyone who wants it. 24 h is the window a
              // reefer thinks in for "is anything moving?" — longer
              // windows hide a fresh drift that started today.
              const trend = computeTrend(readings, k, { windowHours: 24 });
              const trendArrow =
                trend?.direction === 'rising'
                  ? '↑'
                  : trend?.direction === 'falling'
                    ? '↓'
                    : trend?.direction === 'flat'
                      ? '→'
                      : null;
              // Freshness + delta — the two questions a reefer asks first.
              // "0.1 over · 2h ago" answers "how far off" and "can I
              // trust this read" in one mono line.
              // A number too old (or of unknown age) to trust gets no
              // "how far off", no envelope note and no fix: acting on it is
              // how a six-week-old phosphate turned into a dose.
              const deltaLabel =
                latest && isGradedParam(tank, k) && !untrusted
                  ? paramDeltaLabel(k, latest.value, p.target, { displayUnits, salinityUnit })
                  : null;
              const tridentSplit =
                latest && isGradedParam(tank, k) && !untrusted
                  ? outrankedTridentClient(byParam[k] || [], k)
                  : null;
              const ageLabel = latest?.ts ? relativeTimeShort(latest.ts) : null;
              const stale = latest?.ts ? isReadingStale(k, latest.ts) : false;
              const isOpen = quickAdd?.param === k;
              const card = (
                <div
                  key={k}
                  data-drag-key={k}
                  style={{
                    position: 'relative',
                    borderRadius: 14,
                    padding: 12,
                    // An untrusted reading (stuck / frozen / silent source)
                    // tints the WHOLE card amber — a stuck Apex probe posts
                    // fresh timestamps with the same number, so without
                    // this the card looked perfectly healthy except for
                    // one small pill (user-reported).
                    background: untrusted
                      ? 'linear-gradient(180deg, rgba(255, 193, 102, 0.08), rgba(255, 193, 102, 0.02))'
                      : 'var(--surface)',
                    border: `1px solid ${
                      isOpen
                        ? 'var(--accent-border)'
                        : untrusted
                          ? 'rgba(255, 193, 102, 0.55)'
                          : 'var(--surface-border)'
                    }`,
                    boxShadow: 'var(--glass-shadow)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        minWidth: 0,
                      }}
                    >
                      <div
                        className="font-heading hv-param-title"
                        style={{
                          fontSize: 16,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          color: 'var(--text)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {p.label}
                      </div>
                      <span className={`hv-param-pill hv-param-pill-${pillTone}`} title={cardTitle}>
                        {pillLabel}
                      </span>
                      {!editingLayout &&
                        explainParam(k, {
                          subtype: tankSubtype(tank),
                          cfg: config?.params?.explainers || null,
                        }) && (
                          <button
                            type="button"
                            className="hv-explain-btn"
                            onClick={() => {
                              setExplainKey(k);
                              track('param.explain', 'home', { param: k });
                            }}
                            aria-label={`What is ${p.label}?`}
                            title={`What is ${p.label}?`}
                          >
                            ?
                          </button>
                        )}
                      {/* A keeper's own parameter: rename it, change its unit or
                          delete it (feedback #66), in Tank settings at its row. */}
                      {!editingLayout &&
                        tank.role === 'owner' &&
                        isOwnParam(k, tank.params?.[k], tank.subtype_slug) && (
                          <button
                            type="button"
                            className="hv-param-edit-btn"
                            onClick={() =>
                              window.dispatchEvent(
                                new CustomEvent(OPEN_SETTINGS_EVENT, {
                                  detail: { section: 'parameters', focus: k },
                                })
                              )
                            }
                            aria-label={`Edit or delete ${p.label}`}
                          >
                            Edit
                          </button>
                        )}
                    </div>
                    {editingLayout ? (
                      ownsTank ? (
                        <span
                          role="button"
                          tabIndex={0}
                          className="hv-drag-handle compact"
                          onPointerDown={(e) => startParamDrag(e, k, p.label)}
                          onKeyDown={(e) => {
                            if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                              e.preventDefault();
                              moveParam(k, -1);
                            } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                              e.preventDefault();
                              moveParam(k, 1);
                            }
                          }}
                          aria-label={`Drag to move ${p.label}; arrow keys also work`}
                        >
                          <span className="hv-drag-grip" aria-hidden="true">
                            ⠿
                          </span>
                        </span>
                      ) : null
                    ) : isOpen ? (
                      <button
                        onClick={closeQuickAdd}
                        aria-label={`Cancel quick add for ${p.label}`}
                        className="font-heading"
                        style={CARD_ICON_HIT}
                      >
                        <span
                          style={{
                            ...CARD_ICON_DOT,
                            color: 'var(--text-muted)',
                          }}
                        >
                          ×
                        </span>
                      </button>
                    ) : tank.role === 'viewer' ? null : (
                      <button
                        onClick={() => openQuickAdd(k)}
                        aria-label={`Quick add ${p.label} reading`}
                        className="font-heading"
                        style={CARD_ICON_HIT}
                      >
                        <span
                          style={{
                            ...CARD_ICON_DOT,
                            width: 28,
                            height: 28,
                            color: 'var(--cyan)',
                            border: '1px solid var(--accent-border)',
                            fontSize: 18,
                            fontWeight: 500,
                          }}
                        >
                          +
                        </span>
                      </button>
                    )}
                  </div>
                  {isOpen ? (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
                        {METHODS_FOR(k).map((m) => {
                          const active = m.k === quickAdd.method;
                          return (
                            <button
                              key={m.k}
                              type="button"
                              onClick={() => pickMethod(m.k)}
                              className="font-heading qa-tap"
                              title={
                                m.k === 'manual' ? 'Type the value yourself' : 'Opens the camera'
                              }
                              style={{
                                padding: '3px 8px',
                                borderRadius: 999,
                                background: active ? 'var(--cyan)' : 'transparent',
                                color: active ? 'var(--cyan-text)' : 'var(--text-muted)',
                                border: `1px solid ${active ? 'var(--cyan)' : 'var(--surface-border)'}`,
                                fontSize: 10,
                                letterSpacing: '0.18em',
                                textTransform: 'uppercase',
                              }}
                            >
                              {m.label}
                            </button>
                          );
                        })}
                      </div>

                      {quickAdd.photoUrl && (
                        <div
                          style={{
                            position: 'relative',
                            marginBottom: 8,
                            borderRadius: 10,
                            overflow: 'hidden',
                            border: '1px solid var(--surface-border)',
                          }}
                        >
                          <img
                            src={quickAdd.photoUrl}
                            alt=""
                            style={{
                              width: '100%',
                              maxHeight: 90,
                              objectFit: 'cover',
                              display: 'block',
                            }}
                          />
                          {quickAdd.analyzing && (
                            <div
                              className="font-heading"
                              style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'rgba(8,20,54,0.72)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 4,
                                color: 'var(--cyan)',
                                fontSize: 11,
                                letterSpacing: '0.2em',
                                textTransform: 'uppercase',
                              }}
                            >
                              <CoralMark
                                className="sensei-pulse small"
                                size={16}
                                style={{ marginRight: 6 }}
                              />
                              Sensei is reading…
                            </div>
                          )}
                          {!quickAdd.analyzing && (
                            <div
                              style={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                display: 'flex',
                                gap: 4,
                              }}
                            >
                              <button
                                type="button"
                                onClick={openCamera}
                                className="font-heading qa-tap"
                                style={{
                                  padding: '2px 6px',
                                  borderRadius: 999,
                                  background: 'rgba(0,0,0,0.6)',
                                  color: '#fff',
                                  fontSize: 10,
                                  letterSpacing: '0.18em',
                                  textTransform: 'uppercase',
                                }}
                              >
                                Retake
                              </button>
                              <button
                                type="button"
                                onClick={openGallery}
                                className="font-heading qa-tap"
                                style={{
                                  padding: '2px 6px',
                                  borderRadius: 999,
                                  background: 'rgba(0,0,0,0.6)',
                                  color: '#fff',
                                  fontSize: 10,
                                  letterSpacing: '0.18em',
                                  textTransform: 'uppercase',
                                }}
                              >
                                Gallery
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {quickAdd.method !== 'manual' &&
                        !quickAdd.photoUrl &&
                        !quickAdd.analyzing && (
                          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                            <button
                              type="button"
                              onClick={openCamera}
                              className="font-heading hv-capture-btn"
                            >
                              Camera
                            </button>
                            <button
                              type="button"
                              onClick={openGallery}
                              className="font-heading hv-capture-btn"
                            >
                              Gallery
                            </button>
                          </div>
                        )}

                      {quickAdd.param === 'phosphate' && (
                        // Test-type toggle. A lot of reefers use the Hanna
                        // Phosphorus checker (HI-736) which reports as P
                        // in ppb; the stoichiometric P → PO4 conversion
                        // (× 3.066) lands them in our canonical PO4 ppm.
                        // Storage stays PO4; we tag kit + note so the
                        // original number is recoverable on inspection.
                        <div
                          className="font-heading"
                          role="tablist"
                          aria-label="Phosphate test type"
                          style={{
                            display: 'inline-flex',
                            alignSelf: 'flex-start',
                            marginBottom: 6,
                            padding: 2,
                            borderRadius: 999,
                            border: '1px solid var(--surface-border)',
                            background: 'var(--surface)',
                          }}
                        >
                          {[
                            { k: 'po4', label: 'PO₄' },
                            { k: 'p', label: 'Hanna P' },
                          ].map((u) => {
                            const active = (quickAdd.phosphateAs || 'po4') === u.k;
                            return (
                              <button
                                key={u.k}
                                type="button"
                                role="tab"
                                className="qa-tap"
                                aria-selected={active}
                                onClick={() =>
                                  setQuickAdd(
                                    (q) => q && { ...q, phosphateAs: u.k, outlierPrompt: null }
                                  )
                                }
                                style={{
                                  padding: '4px 10px',
                                  borderRadius: 999,
                                  background: active ? 'var(--cyan)' : 'transparent',
                                  color: active ? 'var(--cyan-text)' : 'var(--text-muted)',
                                  fontSize: 11,
                                  letterSpacing: '0.16em',
                                  textTransform: 'uppercase',
                                  border: 'none',
                                }}
                              >
                                {u.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {quickAdd.param === 'phosphate' && quickAdd.phosphateAs === 'p' && (
                        <p
                          style={{
                            fontSize: 13,
                            color: 'var(--text-muted)',
                            margin: '2px 0 6px',
                          }}
                        >
                          Your number is treated as Phosphorus (ppb, the Hanna HI-736 scale) and
                          converted to PO₄ via × 3.066 ÷ 1000 on save.
                        </p>
                      )}
                      {quickAdd.param === 'salinity' && (
                        <div
                          className="font-heading"
                          role="tablist"
                          aria-label="Salinity input unit"
                          style={{
                            display: 'inline-flex',
                            alignSelf: 'flex-start',
                            marginBottom: 6,
                            padding: 2,
                            borderRadius: 999,
                            border: '1px solid var(--surface-border)',
                            background: 'var(--surface)',
                          }}
                        >
                          {SALINITY_UNITS.map((u) => {
                            const active = (quickAdd.inputUnit || 'ppt') === u.k;
                            return (
                              <button
                                key={u.k}
                                type="button"
                                role="tab"
                                className="qa-tap"
                                aria-selected={active}
                                onClick={() => {
                                  writeSalinityUnit(u.k);
                                  setQuickAdd(
                                    (q) =>
                                      q && {
                                        ...q,
                                        inputUnit: u.k,
                                        value: '',
                                        error: '',
                                        outlierPrompt: null,
                                      }
                                  );
                                }}
                                style={{
                                  padding: '4px 10px',
                                  borderRadius: 999,
                                  background: active ? 'var(--cyan)' : 'transparent',
                                  color: active ? 'var(--cyan-text)' : 'var(--text-muted)',
                                  fontSize: 11,
                                  letterSpacing: '0.16em',
                                  textTransform: 'uppercase',
                                  border: 'none',
                                }}
                              >
                                {u.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {/* A real form, not a div: phone keyboards submit
                        forms via their Done/Go key (Android numeric, iOS
                        text layouts), which is the only "Enter" a phone
                        has — the bare onKeyDown alone never fired there
                        (user-reported). */}
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          saveQuickAdd();
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <input
                          autoFocus={quickAdd.method === 'manual'}
                          type="number"
                          inputMode="decimal"
                          enterKeyHint="done"
                          // Never a fixed step: a photo fills S.G. at four places
                          // (1.0274), and a three-place step then refused the 1.026
                          // the keeper corrects it to, inside this form.
                          step="any"
                          value={quickAdd.value}
                          onChange={(e) =>
                            setQuickAdd(
                              (q) =>
                                q && {
                                  ...q,
                                  value: e.target.value,
                                  error: '',
                                  moveToSalinity: null,
                                  outlierPrompt: null,
                                }
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveQuickAdd();
                            }
                          }}
                          placeholder={
                            quickAdd.param === 'salinity' && quickAdd.inputUnit === 'sg'
                              ? '1.026'
                              : quickAdd.param === 'phosphate' && quickAdd.phosphateAs === 'p'
                                ? // PO₄ target shown as its Hanna-P (ppb) equivalent, so the
                                  // hint matches the ppb scale the field now expects.
                                  String(Math.round((p.target / 3.066) * 1000))
                                : quickAdd.param === 'temp' && displayUnits.temp === 'C'
                                  ? String(Math.round(fToC(p.target) * 10) / 10)
                                  : // ORP and a keeper's own test can have no target.
                                    p.target != null
                                    ? String(p.target)
                                    : ''
                          }
                          className="font-mono"
                          style={{
                            flex: 1,
                            minWidth: 0,
                            minHeight: 44,
                            padding: '6px 8px',
                            borderRadius: 8,
                            background: 'transparent',
                            color: 'var(--text)',
                            border: '1px solid var(--surface-border)',
                            outline: 'none',
                            fontSize: 20,
                            fontWeight: 500,
                          }}
                        />
                        <span
                          className="font-mono"
                          style={{ fontSize: 12, color: 'var(--text-dim)' }}
                        >
                          {quickAddUnitLabel({
                            ...quickAdd,
                            unit: p.unit,
                            tempUnit: displayUnits.temp,
                          })}
                        </span>
                        <button
                          type="submit"
                          disabled={quickAdd.saving || quickAdd.analyzing || quickAdd.value === ''}
                          className="font-heading"
                          style={{
                            minHeight: 44,
                            minWidth: 64,
                            padding: '0 16px',
                            borderRadius: 999,
                            background: 'var(--cyan)',
                            color: 'var(--cyan-text)',
                            fontSize: 15,
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            opacity:
                              quickAdd.saving || quickAdd.analyzing || quickAdd.value === ''
                                ? 0.4
                                : 1,
                          }}
                        >
                          {quickAdd.saving ? '…' : 'Save'}
                        </button>
                      </form>
                      {(quickAdd.method === 'manual' || quickAdd.method === 'strip') &&
                        dropKitHint(quickAdd.param, quickAdd.kit) && (
                          <p
                            style={{
                              fontSize: 14,
                              color: 'var(--text-muted)',
                              marginTop: 6,
                              lineHeight: 1.45,
                            }}
                          >
                            {dropKitHint(quickAdd.param, quickAdd.kit)}
                          </p>
                        )}
                      {quickAdd.confidence != null && (
                        <p
                          className="font-mono"
                          style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6 }}
                        >
                          Sensei read{' '}
                          <strong style={{ color: 'var(--cyan)' }}>
                            {quickAdd.value}{' '}
                            {quickAddUnitLabel({
                              ...quickAdd,
                              unit: p.unit,
                              tempUnit: displayUnits.temp,
                            })}
                          </strong>{' '}
                          at {Math.round(quickAdd.confidence * 100)}% — verify before saving.
                        </p>
                      )}
                      {quickAdd.note && (
                        <p
                          style={{
                            fontSize: 12,
                            color: 'var(--text-muted)',
                            marginTop: 4,
                            lineHeight: 1.4,
                          }}
                        >
                          {quickAdd.note}
                        </p>
                      )}
                      <input
                        type="text"
                        value={quickAdd.kit}
                        onChange={(e) => setQuickAdd((q) => q && { ...q, kit: e.target.value })}
                        placeholder="Kit / brand (optional)"
                        className="qa-tap"
                        maxLength={80}
                        style={{
                          width: '100%',
                          marginTop: 8,
                          padding: '5px 8px',
                          borderRadius: 6,
                          background: 'transparent',
                          color: 'var(--text)',
                          border: '1px solid var(--surface-border)',
                          outline: 'none',
                          fontSize: 13,
                          fontFamily: 'inherit',
                        }}
                      />
                      {quickAdd.error && (
                        <p
                          role="alert"
                          style={{
                            fontSize: 16,
                            lineHeight: 1.45,
                            color: 'var(--alert)',
                            marginTop: 6,
                          }}
                        >
                          {quickAdd.error}
                        </p>
                      )}
                      {quickAdd.moveToSalinity &&
                        visibleParams.some((v) => v.key === 'salinity') && (
                          <button
                            type="button"
                            onClick={() =>
                              setQuickAdd({
                                param: 'salinity',
                                value: quickAdd.moveToSalinity,
                                method: 'manual',
                                kit: '',
                                analyzing: false,
                                confidence: null,
                                note: '',
                                photoUrl: null,
                                saving: false,
                                error: '',
                                inputUnit: 'sg',
                              })
                            }
                            style={{
                              marginTop: 8,
                              minHeight: 44,
                              padding: '10px 16px',
                              borderRadius: 999,
                              border: '1px solid var(--cyan)',
                              background: 'transparent',
                              color: 'var(--cyan)',
                              fontSize: 15,
                              cursor: 'pointer',
                            }}
                          >
                            Log {quickAdd.moveToSalinity} as salinity
                          </button>
                        )}
                      {quickAdd.outlierPrompt && (
                        <div
                          ref={revealPrompt}
                          role="alert"
                          style={{
                            marginTop: 6,
                            padding: '8px 10px',
                            borderRadius: 8,
                            border: '1px solid rgba(255, 193, 102, 0.55)',
                            background: 'rgba(255, 193, 102, 0.08)',
                          }}
                        >
                          <p style={{ fontSize: 16, margin: 0, lineHeight: 1.45 }}>
                            {quickAdd.outlierPrompt}
                          </p>
                          <button
                            type="button"
                            className="ts-chip"
                            onClick={() => saveQuickAdd(true)}
                            style={{ marginTop: 6, minHeight: 44, padding: '0 18px' }}
                          >
                            Yes, save it
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        if (!latest || !onOpenHistory) return;
                        track('history.open', 'home', { param: k });
                        onOpenHistory(k);
                      }}
                      disabled={!latest}
                      aria-label={latest ? `${p.label} history` : undefined}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: 0,
                        margin: 0,
                        background: 'transparent',
                        border: 'none',
                        cursor: latest ? 'pointer' : 'default',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'space-between',
                          marginTop: 4,
                          gap: 6,
                        }}
                      >
                        {/* Size lives in CSS (.hv-param-value) so the phone
                          breakpoint can go bigger still — the value is THE
                          glanceable datum on this card. Arrow next to the
                          unit so direction reads at a glance without
                          fighting the number for size. */}
                        <div
                          className="font-mono hv-param-value"
                          style={{
                            lineHeight: 1,
                            fontWeight: 500,
                            opacity: stale || untrusted ? 0.8 : 1,
                            // Untrusted values never wear a status colour —
                            // a stuck probe reading "OK" in healthy cyan is
                            // exactly the lie this card must not tell.
                            color: untrusted
                              ? 'var(--text-dim)'
                              : st === 'off'
                                ? 'var(--alert)'
                                : st === 'ok'
                                  ? 'var(--cyan)'
                                  : 'var(--text-dim)',
                          }}
                        >
                          {latest
                            ? k === 'salinity'
                              ? formatSalinity(
                                  latest.value,
                                  salinityUnit,
                                  sgPlaces(p.acceptable, latest.value)
                                )
                              : k === 'temp' && displayUnits.temp === 'C'
                                ? formatTemp(latest.value, 'C', { withUnit: false })
                                : formatCardValue(k, latest.value)
                            : '—'}
                          <span className="hv-param-unit">
                            {k === 'salinity'
                              ? salinityUnitLabel(salinityUnit)
                              : k === 'temp' && displayUnits.temp === 'C'
                                ? '°C'
                                : p.unit}
                            {trendArrow && (
                              <span
                                className="hv-param-arrow"
                                aria-label={`trend ${trend.direction}`}
                                title={`Trending ${trend.direction}`}
                              >
                                {trendArrow}
                              </span>
                            )}
                          </span>
                        </div>
                        {untrusted && (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#ffc166"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            role="img"
                            aria-label={`${p.label} reading cannot be trusted`}
                            style={{
                              flexShrink: 0,
                              alignSelf: 'center',
                              stroke: 'var(--amber, #ffc166)',
                            }}
                          >
                            <title>{stalePillTitle}</title>
                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                          </svg>
                        )}
                        <Sparkline data={history} status={st} w={72} h={22} />
                      </div>
                      {/* Status line: delta from target + how recent. The
                        two questions a reefer asks before anything else
                        — "how far off?" and "can I trust this read?" —
                        answered in one mono line. */}
                      {latest && (deltaLabel || ageLabel) && (
                        <div className="font-mono hv-param-meta">
                          {deltaLabel && (
                            <span
                              className={deltaLabel === 'on target' ? 'hv-param-meta-ok' : ''}
                              title={`Target ${
                                k === 'salinity'
                                  ? `${formatSalinity(p.target, salinityUnit)}${salinityUnitLabel(salinityUnit)}`
                                  : k === 'temp' && displayUnits.temp === 'C'
                                    ? `${fToC(p.target).toFixed(1)}°C`
                                    : `${p.target}${p.unit}`
                              }`}
                            >
                              {deltaLabel}
                            </span>
                          )}
                          {deltaLabel && ageLabel && <span aria-hidden="true"> · </span>}
                          {ageLabel && (
                            <span className={stale || untrusted ? 'hv-param-meta-stale' : ''}>
                              {/* An old reading says how old in words; STALE
                                is the pill's word (too old to grade), and
                                "8.4 · 3d ago — stale" beside an OK pill
                                read as two answers to one question. */}
                              {/* A Trident row's time is when it ARRIVED, not
                                when it was tested (shared/trident-trust.js). */}
                              {untrusted && staleReason === 'test_time_unknown'
                                ? `received ${ageLabel}`
                                : stale && !untrusted && !suspectUnchanged
                                  ? `${isAutomatedMethodClient(latest.method) ? 'last reading' : 'last tested'} ${ageLabel}`
                                  : ageLabel}
                              {/* "2m ago" on a stuck probe reads as fresh —
                                say WHY the number can't be trusted right
                                where the eye lands. */}
                              {untrusted
                                ? staleReason === 'stuck'
                                  ? ' — same value every update, probe likely stuck'
                                  : staleReason === 'frozen'
                                    ? " — value hasn't moved in days"
                                    : staleReason === 'test_time_unknown'
                                      ? ' — Trident, test time unknown'
                                      : ' — no fresh data, too old to trust'
                                : suspectUnchanged
                                  ? " — hasn't changed in months, confirm it's current"
                                  : ''}
                            </span>
                          )}
                          {latest.method && (
                            <span
                              className={`hv-param-source hv-param-source-${latest.method}`}
                              title={`Source: ${SOURCE_LABEL[latest.method] || latest.method}`}
                            >
                              {SOURCE_LABEL[latest.method] || latest.method}
                            </span>
                          )}
                        </div>
                      )}
                      {/* The CYCLING pill's reason is a tooltip, which a phone
                        never shows, and a keeper with fish they never listed
                        would read this as all clear. */}
                      {latest && Number(latest.value) > 0 && isCycleQuiet(tank, k) && (
                        <div className="hv-param-meta" style={{ fontSize: 13, lineHeight: 1.4 }}>
                          Logged as your cycle, not alerted. Fish, corals or inverts already in? Add
                          them under Livestock so this alerts you.
                        </div>
                      )}
                      {/* The keeper's own band still drives the pill and the
                        target above. This line is the ONLY place the
                        envelope shows, and it has to say the target is
                        untouched or it reads as a contradiction. */}
                      {latest &&
                        isGradedParam(tank, k) &&
                        !untrusted &&
                        envelopeBreached(k, latest.value, tankSubtype(tank)) && (
                          <div className="hv-param-envelope">
                            {envelopeNote(k, { salinityUnit })}
                          </div>
                        )}
                    </button>
                  )}
                  {/* Action area — paired structure so the Dose CTA has a
                    home instead of floating after the card text. Off-band:
                    fix copy + Dose CTA share one alert-styled box. In-band:
                    Dose chip sits in a thin-divider footer. */}
                  {(() => {
                    const hold = latest && !untrusted ? outlierHold(tank, p, readings) : null;
                    const retestFirst = !!hold?.retestOnly;
                    const fixText =
                      st !== 'off' || !latest || !p.acceptable || untrusted
                        ? null
                        : tridentSplit
                          ? 'Your Trident reads differently, so test again before acting on this.'
                          : retestFirst
                            ? hold.sentence
                            : actionableUpdateFor(
                                k,
                                latest.value,
                                p,
                                latest.value < p.acceptable[0] ? 'low' : 'high',
                                tank,
                                null,
                                { salinityUnit, tempUnit: displayUnits.temp }
                              );
                    // No dose offer on a param the tank only logs: a cycling
                    // tank's alk, or reef chemistry in a QT.
                    const showDose =
                      DOSABLE_PARAMS.has(k) &&
                      latest &&
                      !quickAdd &&
                      isGradedParam(tank, k) &&
                      canAct;
                    if (!fixText && !showDose) return null;
                    // "Test again before acting" sits alone: a big DOSE
                    // beside it reads as a prompt to act. The small pill
                    // below stays, and the dose sheet asks first.
                    const actionDose = showDose && !tridentSplit && !retestFirst;
                    if (fixText) {
                      return (
                        <>
                          <div
                            className={`hv-param-action${actionDose ? ' hv-param-action--with-cta' : ''}`}
                          >
                            <div className="hv-param-action-text">{fixText}</div>
                            {actionDose && (
                              <button
                                type="button"
                                onClick={() => {
                                  track('dose.preview', 'home', { param: k });
                                  setDoseParam(k);
                                }}
                                className="font-heading hv-param-action-cta"
                                aria-label={`Calculate ${p.label} dose`}
                              >
                                <span aria-hidden="true">⚗</span> Dose
                              </button>
                            )}
                          </div>
                          {showDose && !actionDose && (
                            <div className="hv-param-footer">
                              <button
                                type="button"
                                onClick={() => setDoseParam(k)}
                                className="font-heading hv-dose-pill"
                                aria-label={`Calculate ${p.label} dose`}
                              >
                                <span aria-hidden="true">⚗</span> Dose
                              </button>
                            </div>
                          )}
                        </>
                      );
                    }
                    return (
                      <div className="hv-param-footer">
                        <button
                          type="button"
                          onClick={() => setDoseParam(k)}
                          className="font-heading hv-dose-pill"
                          aria-label={`Calculate ${p.label} dose`}
                        >
                          <span aria-hidden="true">⚗</span> Dose
                        </button>
                      </div>
                    );
                  })()}
                </div>
              );
              if (!compactParams) return card;
              // A test that needs the keeper stays a full card: HIGH or LOW
              // (Today says "Tap DOSE on the alkalinity card"), and a cycle
              // reading above zero, whose "already have fish?" line is the
              // only warning a quiet cycle gets. Show less closes it until
              // a newer reading.
              const cycleWarn = !!latest && Number(latest.value) > 0 && isCycleQuiet(tank, k);
              const needsKeeper = pillTone === 'off' || cycleWarn;
              const readingKey = latest ? `${latest.id || ''}|${latest.ts}` : '';
              if (openParam === k || isOpen || (needsKeeper && closedParams[k] !== readingKey)) {
                return (
                  <div
                    key={k}
                    className="hv-param-open"
                    tabIndex={-1}
                    ref={(el) => {
                      if (el && focusParamRef.current === `card:${k}`) {
                        focusParamRef.current = null;
                        el.focus({ preventScroll: true });
                      }
                    }}
                  >
                    {card}
                    {/* Not while the quick add is open: its own Cancel
                      says so, and a value typed would be lost here. */}
                    {!isOpen && (
                      <button
                        type="button"
                        className="ts-chip hv-param-less"
                        aria-label={`Show less of ${p.label}`}
                        onClick={() => {
                          focusParamRef.current = `row:${k}`;
                          if (openParam === k) setOpenParam(null);
                          if (needsKeeper) setClosedParams((c) => ({ ...c, [k]: readingKey }));
                        }}
                      >
                        Show less
                      </button>
                    )}
                  </div>
                );
              }
              const rowValue = latest
                ? k === 'salinity'
                  ? formatSalinity(latest.value, salinityUnit, sgPlaces(p.acceptable, latest.value))
                  : k === 'temp' && displayUnits.temp === 'C'
                    ? formatTemp(latest.value, 'C', { withUnit: false })
                    : formatCardValue(k, latest.value)
                : '—';
              const rowUnit =
                k === 'salinity'
                  ? salinityUnitLabel(salinityUnit)
                  : k === 'temp' && displayUnits.temp === 'C'
                    ? '°C'
                    : p.unit;
              // The age a row can show without misleading: "just now" on a
              // stuck probe reads as fresh, and a Trident time is when the
              // result arrived, so those say why instead (as the card does).
              const rowAge = !latest
                ? ''
                : untrusted
                  ? staleReason === 'test_time_unknown'
                    ? `received ${ageLabel}`
                    : staleReason === 'stuck'
                      ? 'probe likely stuck'
                      : staleReason === 'frozen'
                        ? 'not moving'
                        : ageLabel || ''
                  : suspectUnchanged
                    ? 'confirm it'
                    : ageLabel || '';
              // An in-range number with a quieter warning on its card (the
              // published-practice note, a value unchanged for months) keeps
              // the amber edge, so the row is never all clear.
              const envelopeWarn =
                !!latest &&
                isGradedParam(tank, k) &&
                !untrusted &&
                envelopeBreached(k, latest.value, tankSubtype(tank));
              const amber = untrusted || suspectUnchanged || envelopeWarn;
              // One line per test (owner, 2026-10-21): eight cards took about
              // three screens on an iPhone. A tap opens the full card in place,
              // with its quick add, "What is this?", Dose and history.
              return (
                <button
                  key={k}
                  type="button"
                  className="hv-param-row"
                  onClick={() => {
                    focusParamRef.current = `card:${k}`;
                    setOpenParam(k);
                  }}
                  ref={(el) => {
                    if (el && focusParamRef.current === `row:${k}`) {
                      focusParamRef.current = null;
                      el.focus({ preventScroll: true });
                    }
                  }}
                  aria-expanded={false}
                  aria-label={`${p.label}: ${pillLabel}${latest ? `, ${rowValue} ${rowUnit}` : ''}${
                    rowAge ? `, ${rowAge}` : ''
                  }${envelopeWarn ? ', outside published practice' : ''}. Show the card`}
                  title={cardTitle}
                  style={amber ? { borderColor: 'rgba(255, 193, 102, 0.55)' } : undefined}
                >
                  <span className="font-heading hv-param-row-name">{p.label}</span>
                  <span className={`hv-param-pill hv-param-pill-${pillTone}`}>{pillLabel}</span>
                  <span
                    className="font-mono hv-param-row-value"
                    style={{
                      color: untrusted
                        ? 'var(--text-dim)'
                        : st === 'off'
                          ? 'var(--alert)'
                          : st === 'ok'
                            ? 'var(--cyan)'
                            : 'var(--text-dim)',
                    }}
                  >
                    {rowValue}
                    <span className="hv-param-row-unit">
                      {rowUnit}
                      {trendArrow ? ` ${trendArrow}` : ''}
                    </span>
                  </span>
                  <span
                    className={`hv-param-row-age${untrusted || suspectUnchanged ? ' hv-param-row-age--warn' : ''}`}
                  >
                    {rowAge}
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {show('today') && (
        <section
          className="slide-in span-2 hv-cell hv-cell-today"
          data-drag-key="today"
          style={{ paddingBottom: 10, order: orderOf('today') }}
        >
          {editingLayout && (
            <LayoutChrome
              k="today"
              onDragStart={startSectionDrag}
              move={moveSection}
              onHide={hideSection}
            />
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <h2
              className="font-heading hv-section-title"
              style={{ fontSize: 17, letterSpacing: '0.08em' }}
            >
              TODAY
            </h2>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button
                onClick={() => onTab('tasks')}
                className="font-heading hv-today-head-btn"
                style={{
                  fontSize: 11,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: 'var(--text-muted)',
                }}
              >
                See all →
              </button>
            </div>
          </div>
          {tank.role !== 'viewer' && (
            <div className="hv-today-tools">
              <button
                type="button"
                className="hv-today-tool font-heading"
                onClick={() => setCareSheet({ kind: 'ask' })}
              >
                Ask TankSensei
              </button>
            </div>
          )}
          {todayAlerts.length > 0 && (
            <ul className="hv-today-alerts">
              {todayAlerts.map((a) => (
                <li
                  key={a.key}
                  className={`hv-today-alert-item${a.info ? ' hv-today-alert-item--info' : ''}`}
                >
                  <button
                    type="button"
                    className="hv-today-alert"
                    onClick={
                      editingLayout || !onOpenHistory ? undefined : () => onOpenHistory(a.paramKey)
                    }
                  >
                    <span className="hv-today-alert-mark" aria-hidden="true">
                      <CoralMark size={16} />
                    </span>
                    <span className="hv-today-alert-body">
                      <span className="hv-today-alert-title">{a.title}</span>
                      <span className="hv-today-alert-text">{a.text}</span>
                    </span>
                  </button>
                  {tank.role !== 'viewer' && !editingLayout && !a.info && (
                    <button
                      type="button"
                      className="hv-today-alert-followup font-heading"
                      aria-label={`Follow up on ${a.label}`}
                      onClick={() =>
                        setCareSheet({
                          kind: 'followup',
                          start: 'new',
                          prefill: { title: `Check ${a.label}`, param: a.paramKey },
                        })
                      }
                    >
                      Follow up
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {unlinkedHydros.length > 0 && !editingLayout && (
            <ul className="hv-today-alerts">
              {unlinkedHydros.map((c) => {
                const row = unlinkedControllerRow(c);
                return (
                  <li key={c.thing_id} className="hv-today-alert-item hv-today-alert-item--info">
                    <button
                      type="button"
                      className="hv-today-alert"
                      onClick={() =>
                        window.dispatchEvent(
                          new CustomEvent(OPEN_ACCOUNT_EVENT, { detail: 'hydros-link' })
                        )
                      }
                    >
                      <span className="hv-today-alert-mark" aria-hidden="true">
                        <CoralMark size={16} />
                      </span>
                      <span className="hv-today-alert-body">
                        <span className="hv-today-alert-title">{row.title}</span>
                        <span className="hv-today-alert-text">{row.text}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {quietDevices.length > 0 && !editingLayout && (
            <ul className="hv-today-alerts">
              {quietDevices.map(({ id, row }) => (
                <li key={id} className="hv-today-alert-item hv-today-alert-item--info">
                  <button
                    type="button"
                    className="hv-today-alert"
                    onClick={() =>
                      window.dispatchEvent(
                        new CustomEvent(OPEN_SETTINGS_EVENT, {
                          detail: { section: 'equipment', focus: `device:${id}` },
                        })
                      )
                    }
                  >
                    <span className="hv-today-alert-mark" aria-hidden="true">
                      <CoralMark size={16} />
                    </span>
                    <span className="hv-today-alert-body">
                      <span className="hv-today-alert-title">{row.title}</span>
                      <span className="hv-today-alert-text">{row.text}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {todayInfoOnly && plan.length === 0 ? (
            <div
              style={{
                padding: 16,
                textAlign: 'center',
                borderRadius: 12,
                background: 'var(--surface)',
                border: '1px solid var(--surface-border)',
                fontSize: 15,
                color: 'var(--text-muted)',
                lineHeight: 1.5,
              }}
            >
              {hasAnyReading &&
              (todayAlerts.length || unlinkedHydros.length || quietDevices.length) ? (
                <>Nothing else needs you today.</>
              ) : hasAnyReading ? (
                <>
                  Nothing needs you today.
                  <div style={{ marginTop: 4 }}>
                    {allTestsInRange ? (
                      <>All parameters are tracking in range. Keep doing what you&apos;re doing.</>
                    ) : (
                      'Nothing off in what you have logged so far.'
                    )}
                  </div>
                </>
              ) : (
                <>
                  No readings yet.
                  {canAct && (
                    <div style={{ marginTop: 4 }}>Log a test to see how the tank is doing.</div>
                  )}
                </>
              )}
            </div>
          ) : plan.length === 0 ? null : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {plan.map((item) => (
                <div
                  key={`${item.kind}-${item.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    borderRadius: 12,
                    background: 'var(--surface)',
                    border: `1px solid ${item.overdue ? 'var(--alert-border)' : 'var(--surface-border)'}`,
                  }}
                >
                  {item.kind === 'task' ? (
                    <button
                      onClick={() =>
                        item.followUpPlanId
                          ? setCareSheet({ kind: 'followup', planId: item.followUpPlanId })
                          : courseTaskIds.has(item.id) && onOpenTreatments
                            ? onOpenTreatments()
                            : completeTask(item.id)
                      }
                      aria-label={
                        item.followUpPlanId || courseTaskIds.has(item.id)
                          ? `Open ${item.title}`
                          : `Complete ${item.title}`
                      }
                      disabled={tickLocked(item)}
                      className="hv-task-check"
                      style={tickLocked(item) ? { ...CARD_ICON_HIT, opacity: 0.4 } : CARD_ICON_HIT}
                    >
                      <span
                        style={{
                          ...CARD_ICON_DOT,
                          border: `1.5px solid ${item.overdue ? 'var(--alert)' : 'var(--cyan)'}`,
                        }}
                      >
                        {completing === item.id && (
                          <svg width="12" height="12" viewBox="0 0 24 24" className="spin">
                            <circle
                              cx="12"
                              cy="12"
                              r="9"
                              fill="none"
                              stroke="var(--cyan)"
                              strokeWidth="3"
                              strokeDasharray="14 28"
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                      </span>
                    </button>
                  ) : (
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        background: 'var(--violet-bg)',
                        border: '1px solid var(--violet-border)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--violet)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                      </svg>
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 17, color: 'var(--text)' }}>{item.title}</div>
                    <div
                      className="font-mono"
                      style={{
                        fontSize: 14,
                        color: item.overdue ? 'var(--alert)' : 'var(--text-dim)',
                        marginTop: 1,
                      }}
                    >
                      {item.sub}
                    </div>
                  </div>
                  {canAct && item.kind === 'task' && /water\s*change/i.test(item.title || '') && (
                    <button
                      onClick={() => setShowWaterChange(true)}
                      className="font-heading hv-row-btn"
                      style={{
                        fontSize: 11,
                        letterSpacing: '0.2em',
                        textTransform: 'uppercase',
                        color: 'var(--cyan-text)',
                        background: 'var(--cyan)',
                        padding: '4px 10px',
                        borderRadius: 999,
                        flexShrink: 0,
                      }}
                    >
                      Log it →
                    </button>
                  )}
                  {item.kind === 'task' && item.followUpPlanId && (
                    <button
                      onClick={() =>
                        setCareSheet({ kind: 'followup', planId: item.followUpPlanId })
                      }
                      className="font-heading hv-row-btn"
                      style={{
                        fontSize: 11,
                        letterSpacing: '0.2em',
                        textTransform: 'uppercase',
                        color: 'var(--cyan)',
                        border: '1px solid var(--accent-border)',
                        padding: '4px 10px',
                        borderRadius: 999,
                        flexShrink: 0,
                      }}
                    >
                      Open
                    </button>
                  )}
                  {item.kind === 'supply' &&
                    (() => {
                      // Resolver: explicit reorder_url wins; otherwise try
                      // the library lookup with the part's kind + parent
                      // brand + user's retailer preference. Falls back to
                      // the existing "Review" button when no URL resolves.
                      const url = resolveReorderUrl({
                        reorderUrl: item.reorder_url,
                        partKind: item.part_kind,
                        brand: item.brand,
                        library: config?.reorder,
                        preferredRetailer: user?.preferred_retailer,
                      });
                      if (url) {
                        return (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-heading hv-row-btn"
                            style={{
                              fontSize: 11,
                              letterSpacing: '0.2em',
                              textTransform: 'uppercase',
                              color: 'var(--cyan-text)',
                              background: 'var(--cyan)',
                              padding: '4px 10px',
                              borderRadius: 999,
                              textDecoration: 'none',
                            }}
                          >
                            Reorder →
                          </a>
                        );
                      }
                      return (
                        <button
                          onClick={() => onTab('supplies')}
                          className="font-heading hv-row-btn"
                          style={{
                            fontSize: 11,
                            letterSpacing: '0.2em',
                            textTransform: 'uppercase',
                            color: 'var(--violet)',
                            border: '1px solid var(--violet-border)',
                            padding: '4px 10px',
                            borderRadius: 999,
                          }}
                        >
                          Review
                        </button>
                      );
                    })()}
                  {canAct && item.kind === 'service' && (
                    <button
                      onClick={() => markServiced(item.part_id, item.id)}
                      disabled={completing === item.id}
                      className="font-heading hv-row-btn"
                      style={{
                        fontSize: 11,
                        letterSpacing: '0.2em',
                        textTransform: 'uppercase',
                        color: 'var(--cyan)',
                        border: '1px solid var(--accent-border)',
                        padding: '4px 10px',
                        borderRadius: 999,
                      }}
                    >
                      {completing === item.id ? '…' : 'Done'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {followUps.length > 0 && (
            <button
              type="button"
              className="hv-today-followups"
              onClick={editingLayout ? undefined : () => setCareSheet({ kind: 'followup' })}
            >
              <span className="hv-today-followups-count">
                {followUps.length} follow-up{followUps.length === 1 ? '' : 's'} open
              </span>
              <span className="hv-today-followups-next font-mono">
                Next: {followUps[0].title}, {shortDate(followUps[0].due)}
              </span>
            </button>
          )}
          {courses.length > 0 && onOpenTreatments && (
            <button
              type="button"
              className="hv-today-followups"
              onClick={editingLayout ? undefined : onOpenTreatments}
            >
              <span className="hv-today-followups-count">
                {courses.length} in quarantine or treatment
              </span>
              {courses.map((c) => {
                const prog = courseProgress(c, localDateIso());
                return (
                  <span key={c.id} className="hv-today-followups-next font-mono">
                    {c.title}:{' '}
                    {prog?.planned ? `day ${prog.day} of ${prog.planned}` : `day ${prog?.day ?? 1}`}
                  </span>
                );
              })}
            </button>
          )}
        </section>
      )}

      {show('journal') && (
        <div
          className="hv-cell hv-cell-journal span-2"
          data-drag-key="journal"
          style={{ order: orderOf('journal') }}
        >
          {editingLayout && (
            <LayoutChrome
              k="journal"
              onDragStart={startSectionDrag}
              move={moveSection}
              onHide={hideSection}
            />
          )}
          <JournalSection tank={tank} onReload={onReload} />
        </div>
      )}

      {show('residents') && (
        <div
          className="hv-cell hv-cell-residents span-2"
          data-drag-key="residents"
          style={{ order: orderOf('residents') }}
        >
          {editingLayout && (
            <LayoutChrome
              k="residents"
              onDragStart={startSectionDrag}
              move={moveSection}
              onHide={hideSection}
            />
          )}
          <LivestockResidents
            tank={tank}
            careKinds={residentCareKinds}
            onOpenLivestock={onOpenLivestock}
            onOpenHistory={onOpenLivestockHistory}
            onReload={onReload}
            reordering={editingLayout && tank.role !== 'viewer'}
          />
        </div>
      )}

      {/* "Fact of the day": one reefer fact a day from the pool that
          footers the emails. Renders nothing until the pool loads, so it
          never leaves an empty slot. The pool is reef facts only, so a
          freshwater tank shows none (feedback #74). */}
      {show('tip') && showsReefFact(tank) && (
        <div
          className="hv-cell hv-cell-tip span-2"
          data-drag-key="tip"
          style={{ order: orderOf('tip') }}
        >
          {editingLayout && (
            <LayoutChrome
              k="tip"
              onDragStart={startSectionDrag}
              move={moveSection}
              onHide={hideSection}
            />
          )}
          <TipCard />
        </div>
      )}

      {/* Renders nothing when there's no entry newer than the keeper's
          last "Got it" — so the slot is invisible most of the time and
          appears only when something actually shipped. */}
      <WhatsNewCard order={orderOf('whatsnew')} />

      {doseParam && (
        <DoseCalculator
          tank={tank}
          param={doseParam}
          latestValue={currentReadingsClient(byParam[doseParam] || [], doseParam)[0]?.value ?? null}
          onClose={() => setDoseParam(null)}
          onReload={onReload}
        />
      )}

      {explainKey && (
        <ParamExplainerSheet
          tank={tank}
          paramKey={explainKey}
          param={visibleParams.find((ep) => ep.key === explainKey) || null}
          readings={byParam[explainKey] || []}
          onClose={() => setExplainKey(null)}
        />
      )}

      {showWaterChange && (
        <WaterChangeSheet tank={tank} onClose={() => setShowWaterChange(false)} onDone={onReload} />
      )}

      {careSheet?.kind === 'ask' && (
        <AskSheet tank={tank} onClose={() => setCareSheet(null)} onHistory={onOpenHistory} />
      )}
      {careSheet?.kind === 'followup' && (
        <FollowUpSheet
          tank={tank}
          start={careSheet.start}
          planId={careSheet.planId || null}
          prefill={careSheet.prefill || null}
          onClose={() => setCareSheet(null)}
          onSaved={async () => {
            await Promise.all([onReload(), loadFollowUps()]);
          }}
          onHistory={(param) => {
            setCareSheet(null);
            onOpenHistory?.(param);
          }}
        />
      )}
    </div>
  );
}

// Onboarding-after-onboarding: a new tank with zero readings is a
// blank canvas. Without this card, Home was empty Sensei Score +
// empty chip row + empty param grid — confusing for a brand-new
// keeper. This surfaces the 2-3 things they should do next, phase-
// aware (cycling tank gets "test ammonia", established tank gets
// "log a fresh alk"). Disappears as soon as ANY reading lands.
function FirstRunCard({
  tank,
  readingsCount,
  livestockCount,
  onLog,
  onBrowseLivestock,
  onOpenTankSettings,
}) {
  if (readingsCount > 0) return null;
  const phase = tank?.phase || null;
  const qt = isQuarantineTank(tank);
  const phaseHint = firstRunHint(tank);
  // The catalog is saltwater species: a freshwater keeper adds theirs by name.
  const fresh = isFreshwaterTank(tank);

  // Tank build tunes the dose calculator; a QT is not dosed for reef chemistry.
  const hasWorkingVol = qt || tank?.volume_water_gal != null;

  return (
    <section className="frc-card slide-in" aria-label="Getting started">
      <div className="frc-eyebrow">Sensei&rsquo;s welcome</div>
      <h3 className="frc-title font-heading">A few quick things to get rolling</h3>
      <p className="frc-intro">{phaseHint}</p>

      <ul className="frc-steps">
        <li>
          <button type="button" className="frc-step" onClick={onLog}>
            <span className="frc-step-num">1</span>
            <span className="frc-step-body">
              <strong>Log your first reading</strong>
              <span>
                {fresh
                  ? 'Type it in, or snap a test strip or a Hanna LCD with the camera.'
                  : 'Manual entry, paste an ICP, or snap a Hanna LCD with the camera.'}
              </span>
            </span>
            <span className="frc-step-chev" aria-hidden="true">
              →
            </span>
          </button>
        </li>
        {livestockCount === 0 && (
          <li>
            <button type="button" className="frc-step" onClick={onBrowseLivestock}>
              <span className="frc-step-num">2</span>
              <span className="frc-step-body">
                <strong>
                  {(phase === 'cycling' || phase === 'setup') && !qt
                    ? 'Plan your stock list'
                    : 'Tell Sensei what lives in your tank'}
                </strong>
                <span>
                  {fresh
                    ? 'Add your fish and shrimp by name.'
                    : (phase === 'cycling' || phase === 'setup') && !qt
                      ? 'Browse 285 saltwater species to plan what goes in once the cycle is done.'
                      : 'Browse the catalog or search by name. Verdicts are tailored to your tank.'}
                </span>
              </span>
              <span className="frc-step-chev" aria-hidden="true">
                →
              </span>
            </button>
          </li>
        )}
        {!hasWorkingVol && (
          <li>
            <button type="button" className="frc-step" onClick={onOpenTankSettings}>
              <span className="frc-step-num">{livestockCount === 0 ? 3 : 2}</span>
              <span className="frc-step-body">
                <strong>Set up Tank build (optional, takes 30 seconds)</strong>
                <span>
                  {fresh
                    ? 'Rock, sand, any sump. Gives Sensei the real water volume for water changes.'
                    : 'Sump, rockscape, sand. Makes the dose calculator accurate from day one.'}
                </span>
              </span>
              <span className="frc-step-chev" aria-hidden="true">
                →
              </span>
            </button>
          </li>
        )}
      </ul>
    </section>
  );
}
